/**
 * PlansTab - RRSP / TFSA contribution planning and tracking.
 *
 * Distinct from the Wealth tab: Wealth tracks what these accounts are WORTH,
 * this tracks what you have PUT IN against a target and against your room.
 *
 * Data lives on userData.plans and is saved with saveUserData({ plans }).
 * All date and total maths lives in utils/plansUtils.js - this file is
 * presentation and editing only.
 */
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, Target, CalendarDays, Pencil
} from "lucide-react";
import { formatCurrency, getCurrentCurrency } from "../../utils/formatters";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts"; // dark_09
import {
  Sheet,
  DateField,
  SHEET_SERIF,
  SHEET_FIELD_LABEL,
  SHEET_INPUT_CLS,
  SHEET_INPUT_STYLE,
  SHEET_DATE_STYLE,
  PLAN_TILE_PALETTES,
  MONTH_SHORT,
  prettyDate,
  prettyShort,
  normalizePlans,
  ConfirmDialog,
} from "../plans/plansShared";

import {
  newSource,
  newContribution,
  getRrspWindow,
  summarize,
  paceStatus,
  todayLocalKey,
  isAmbiguousRrspDate,
  findTransferSuggestions,
  TRANSFER_MATCH_DAYS,
  TRANSFER_MATCH_AMOUNT,
  buildReviewItems,
} from "../../utils/plansUtils";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_41

const ACCOUNTS = [
  { key: 'rrsp', label: 'RRSP', emoji: '\ud83c\udde8\ud83c\udde6' },
  { key: 'tfsa', label: 'TFSA', emoji: '\ud83c\udf31' },
];

// plan_41: Money plan tiles - RRSP blue, TFSA mint, then lilac and pink
const PLAN_LOOK_TILES = [
  { bg: '#DFE9F9', label: '#3C6DB5', value: '#3C6DB5' },
  { bg: '#DFF2E7', label: '#2F8756', value: '#2F8756' },
  { bg: '#ECE6FA', label: '#7A5DD3', value: '#7A5DD3' },
  { bg: '#FAE4ED', label: '#C04B7C', value: '#C04B7C' },
];

const FREQ_LABELS = {
  monthly: 'Monthly',
  biweekly: 'Every 2 weeks',
  semimonthly: 'Twice a month',
};

const OneOffSheet = ({ account, taxYear, todayKey, accent, track, muted, sources = [], existing = null, onSave, onDelete, onClose }) => {
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '');
  const [date, setDate] = useState(existing ? existing.date : todayKey);
  const [note, setNote] = useState(existing ? existing.note || '' : '');
  const [claimYear, setClaimYear] = useState(
    existing && existing.taxYear != null ? existing.taxYear : taxYear
  );
  // Defaults to the only source when there is exactly one - picking from a
  // list of one is a question with no information in it.
  const [sourceId, setSourceId] = useState(
    existing ? existing.sourceId || null : sources.length === 1 ? sources[0].id : null
  );
  // Deleting asks twice, in place. A confirm dialog over a bottom sheet is two
  // layers deep and easy to dismiss by accident.
  const [armed, setArmed] = useState(false);
  const label = account === 'rrsp' ? 'RRSP' : 'TFSA';

  // An RRSP contribution made in the first 60 days of a year can be claimed
  // against either that year or the one before, and only the user knows which
  // they intend. Everything else has exactly one home.
  const ambiguous = account === 'rrsp' && isAmbiguousRrspDate(date);
  const priorYear = date ? Number(date.slice(0, 4)) - 1 : taxYear;
  const valid = Number(amount) > 0 && date;

  return (
    <Sheet
      title={existing ? 'Edit this contribution' : 'One-off ' + label + ' contribution'}
      subtitle="A lump sum outside your regular schedule."
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Amount</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
          inputMode="decimal"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Date</label>
        <DateField
          className={SHEET_INPUT_CLS}
          style={SHEET_DATE_STYLE}
          value={date || ''}
          onChange={setDate}
        />
      </div>

      {ambiguous && (
        <div className="mb-4">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Count toward</label>
          <div className="flex gap-1 p-1 rounded-full" style={{ background: track }}>
            {[priorYear, priorYear + 1].map((y) => (
              <button
                key={y}
                onClick={() => setClaimYear(y)}
                className="flex-1 py-2.5 rounded-full text-[13px] font-semibold"
                style={
                  claimYear === y
                    ? { background: '#fff', color: accent, boxShadow: '0 1px 3px rgba(31,41,55,.08)' }
                    : { color: '#6B7280' }
                }
              >
                {y}
              </button>
            ))}
          </div>
          <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
            Contributions made in the first 60 days of a year can be claimed
            against either year. Pick the one you intend to deduct it on.
          </p>
        </div>
      )}

      {sources.length > 0 && (
        <div className="mb-4">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Paid into</label>
          <div className="rounded-2xl overflow-hidden border theme-border-100">
            {sources.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setSourceId(s.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left"
                style={{ borderTop: i === 0 ? 'none' : '1px solid #F5EBF0', background: '#FCF9FB' }}
              >
                <span
                  className="rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    width: '18px', height: '18px',
                    border: '2px solid ' + (sourceId === s.id ? accent : '#DDD3D9'),
                    background: sourceId === s.id ? accent : 'transparent',
                  }}
                >
                  {sourceId === s.id && (
                    <span className="rounded-full" style={{ width: '6px', height: '6px', background: '#fff' }} />
                  )}
                </span>
                <span className="text-[15px]" style={{ color: '#1F2937', fontWeight: 500 }}>{s.name}</span>
              </button>
            ))}
            <button
              onClick={() => setSourceId(null)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              style={{ borderTop: '1px solid #F5EBF0', background: '#FCF9FB' }}
            >
              <span
                className="rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  width: '18px', height: '18px',
                  border: '2px solid ' + (sourceId === null ? accent : '#DDD3D9'),
                  background: sourceId === null ? accent : 'transparent',
                }}
              >
                {sourceId === null && (
                  <span className="rounded-full" style={{ width: '6px', height: '6px', background: '#fff' }} />
                )}
              </span>
              <span className="text-[15px]" style={{ color: '#6B7280' }}>Not from a source</span>
            </button>
          </div>
          <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
            It still counts toward your {label} total either way. Attaching it just
            keeps the source's own figure right.
          </p>
        </div>
      )}

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Note <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '50px' }}
          placeholder="Bonus, tax refund, gift"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <button
        disabled={!valid}
        onClick={() =>
          onSave({
            account,
            amount: Number(amount) || 0,
            date,
            taxYear: ambiguous ? claimYear : taxYear,
            sourceId,
            note: note.trim(),
            id: existing ? existing.id : null,
          })
        }
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {valid
          ? (existing ? 'Save ' + formatCurrency(Number(amount)) : 'Log ' + formatCurrency(Number(amount)))
          : (existing ? 'Save' : 'Log contribution')}
      </button>

      {existing && (
        <>
          <button
            onClick={() => setArmed(true)}
            className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border"
            style={{ color: '#E11D48', borderColor: '#FBCFD8' }}
          >
            Remove this contribution
          </button>
          {armed && (
            <ConfirmDialog
              title="Remove this contribution?"
              body="It comes out of your total for good. Nothing else changes."
              confirmLabel="Remove"
              onCancel={() => setArmed(false)}
              onConfirm={() => { setArmed(false); onDelete(existing.id); }}
            />
          )}
          <p className="text-[12px] text-gray-500 mt-3 leading-snug">
            Removing takes it out of your total for good. Nothing else changes.
          </p>
        </>
      )}
    </Sheet>
  );
};

/**
 * Confirming one scheduled occurrence.
 *
 * Deliberately three choices. Changing an amount permanently, or stopping a
 * source, are edits to the SOURCE and already live in SourceSheet - offering
 * them here as well would give two doors to the same room and make it easy to
 * change next year's contributions while trying to correct last Friday's.
 */
const ConfirmSheet = ({ occ, accent, track, muted, onConfirm, onSkip, onUnconfirm, onClose }) => {
  // Opens on what was actually confirmed when there is one, so correcting a
  // typo does not mean retyping the real figure from memory.
  const [amount, setAmount] = useState(() => {
    const start = occ.state === 'confirmed' ? occ.amount : occ.plannedAmount;
    return start == null ? '' : String(start);
  });
  const n = Number(amount);
  const valid = n > 0;
  // Half a cent of tolerance so re-typing the same figure is not "different".
  const changed = valid && Math.abs(n - Number(occ.plannedAmount)) > 0.005;

  const alreadyConfirmed = occ.state === 'confirmed';
  // Undo applies to a skip too. Nothing here is permanent any more, so the
  // sheet must not keep implying that one branch is.
  const alreadyDecided = alreadyConfirmed || occ.state === 'skipped';

  return (
    <Sheet
      title={
        alreadyConfirmed
          ? 'Change this contribution'
          : occ.state === 'skipped'
            ? 'You skipped this one'
            : 'Did this go in?'
      }
      subtitle={occ.sourceName + ' \u00b7 ' + prettyDate(occ.date)}
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Amount</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
          inputMode="decimal"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
          {changed
            ? 'Scheduled for ' + formatCurrency(Number(occ.plannedAmount) || 0) +
              '. This applies to this one only \u2014 edit the source to change what comes after it.'
            : 'Change it if a different amount actually went in.'}
        </p>
      </div>

      <button
        disabled={!valid}
        onClick={() => onConfirm(n)}
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {valid
          ? (alreadyConfirmed ? 'Save ' + formatCurrency(n) : 'Confirm ' + formatCurrency(n))
          : (alreadyConfirmed ? 'Save' : 'Confirm')}
      </button>

      <button
        onClick={onSkip}
        className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border theme-border-100"
        style={{ color: muted }}
      >
        It didn't happen, skip it
      </button>

      <p className="text-[12px] text-gray-500 mt-3 leading-snug">
        Skipping takes it out of your total and out of what is still to come.
        It stays on your schedule marked as skipped.
      </p>

      {alreadyDecided && (
        <>
          <button
            onClick={onUnconfirm}
            className="w-full rounded-full py-3.5 mt-4 text-[14px] font-semibold border theme-border-100"
            style={{ color: muted }}
          >
            {occ.state === 'skipped' ? 'Undo this skip' : "Undo, I didn't make this"}
          </button>
          <p className="text-[12px] text-gray-500 mt-3 leading-snug">
            Different from skipping: this puts it back to undecided, waiting to be
            confirmed, as though you had never confirmed it.
          </p>
        </>
      )}
    </Sheet>
  );
};

const TargetSheet = ({ account, taxYear, config, accent, track, muted, palette, onSave, onClose }) => {
  const targetsByYear = config.targetsByYear || {};
  const roomByYear = config.roomByYear || {};
  const existingTarget = taxYear in targetsByYear ? (targetsByYear[taxYear] || 0) : (config.target || 0);
  const existingRoom = taxYear in roomByYear
    ? roomByYear[taxYear]
    : (config.roomAvailable != null && taxYear === new Date().getFullYear() ? config.roomAvailable : null);

  // Someone who has been here before and deliberately left the target at zero
  // wanted track-only; anyone new gets the goal option first.
  const [mode, setMode] = useState(
    config.configured && !(existingTarget > 0) ? 'track' : 'goal'
  );
  const [target, setTarget] = useState(existingTarget ? String(existingTarget) : '');
  const [room, setRoom] = useState(existingRoom != null ? String(existingRoom) : '');
  const openingByYear = config.openingByYear || {};
  const [opening, setOpening] = useState(
    openingByYear[taxYear] ? String(openingByYear[taxYear]) : ''
  );
  const label = account === 'rrsp' ? 'RRSP' : 'TFSA';

  // With a goal you are almost certainly running sources, and each source
  // carries its own starting amount - offering an account-level one as well
  // just invites counting the same money twice. Still shown whenever a value
  // exists, so a stored balance can never be stranded somewhere uneditable.
  const showOpening = mode === 'track' || Number(opening) > 0;

  return (
    <Sheet
      title={label + ' for ' + taxYear}
      subtitle="Track what you put in, with or without a goal."
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <div className="flex gap-1 p-1 rounded-full" style={{ background: track }}>
          {[['goal', 'Set a goal'], ['track', 'No goal']].map(([k, lab]) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold"
              style={
                mode === k
                  ? { background: '#fff', color: accent, boxShadow: '0 1px 3px rgba(31,41,55,.08)' }
                  : { color: '#6B7280' }
              }
            >
              {lab}
            </button>
          ))}
        </div>
      </div>

      {mode === 'goal' ? (
        <div className="mb-4">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
            Target for {taxYear}
          </label>
          <input
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
            inputMode="decimal"
            placeholder="0.00"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
      ) : (
        <p className="text-[12.5px] text-gray-500 mb-4 leading-snug px-1">
          No progress bar and no projection. Worthly will just keep a running
          total of what you have put in for {taxYear}.
        </p>
      )}

      {showOpening && (
        <div className="mb-4">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
            Already contributed in {taxYear} <span className="font-normal" style={{ color: muted }}>optional</span>
          </label>
          <input
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
            inputMode="decimal"
            placeholder="0.00"
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
          />
          <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
            What is already in for the year. If you are adding sources, put
            their head start on the source instead so it is not counted twice.
          </p>
          {account === 'rrsp' && (
            <p className="text-[12px] mt-2 leading-snug px-3 py-2.5 rounded-xl" style={{ background: palette[0].bg, color: palette[0].value }}>
              Watch out for January and February. Contributions made then are
              usually claimed against the previous tax year, so leave them out if
              you have already counted them there.
            </p>
          )}
        </div>
      )}

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Contribution room for {taxYear} <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
          inputMode="decimal"
          placeholder="Leave blank to skip"
          value={room}
          onChange={(e) => setRoom(e.target.value)}
        />
        <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
          {account === 'rrsp'
            ? 'Your deduction limit can be found on your notice of assessment for last year, or in your CRA account.'
            : 'Your total available room can be found in your CRA account. TFSA room is not shown on your notice of assessment.'}
        </p>
      </div>

      <button
        onClick={() => onSave(account, mode === 'goal' ? target : 0, room, opening)}
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: accent }}
      >
        Save
      </button>
    </Sheet>
  );
};

const SourceSheet = ({
  account, source, todayKey, accent, track, muted, palette,
  onSave, onStop, onResume, onDelete, onClose,
}) => {
  const [draft, setDraft] = useState(() =>
    source
      ? { ...source }
      : newSource({ account, name: '', frequency: 'monthly', startDate: todayKey })
  );
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const valid = draft.name.trim() && Number(draft.amount) > 0 && draft.startDate;

  // Deleting asks twice, in place - same as removing a one-off. A confirm
  // dialog stacked over a bottom sheet is two layers deep and easy to dismiss
  // by accident, which is the opposite of what a destructive action wants.
  const [armed, setArmed] = useState(false);

  const [changeDate, setChangeDate] = useState('');
  const [changeAmount, setChangeAmount] = useState('');

  const addChange = () => {
    if (!changeDate || !(Number(changeAmount) > 0)) return;
    setDraft((d) => ({
      ...d,
      // Kept sorted so amountOn() reads them in order and the list matches.
      changes: [...(d.changes || []), { effectiveDate: changeDate, amount: Number(changeAmount) }]
        .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1)),
    }));
    setChangeDate('');
    setChangeAmount('');
  };

  const removeChange = (i) => {
    setDraft((d) => ({ ...d, changes: (d.changes || []).filter((_, x) => x !== i) }));
  };

  return (
    <Sheet
      title={source ? 'Edit source' : 'Add a source'}
      subtitle="Where the money comes from. Add one for work and one for personal if you have both."
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Name</label>
        <input
          className={SHEET_INPUT_CLS}
          style={SHEET_INPUT_STYLE}
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Already contributed from this source
        </label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
          inputMode="decimal"
          placeholder="0.00"
          value={draft.startingAmount || ''}
          onChange={(e) => set('startingAmount', e.target.value)}
        />
        <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
          What this source alone had already put in when you started tracking.
          Money not tied to any source goes under Goal &amp; room instead.
        </p>
        {account === 'rrsp' && (
          <p className="text-[12px] mt-2 leading-snug px-3 py-2.5 rounded-xl" style={{ background: palette[0].bg, color: palette[0].value }}>
            Watch out for January and February. Contributions made then are
            usually claimed against the previous tax year, so leave them out
            if you have already counted them there.
          </p>
        )}
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>How often</label>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: track }}>
          {Object.keys(FREQ_LABELS).map((f) => (
            <button
              key={f}
              onClick={() => set('frequency', f)}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold"
              style={
                draft.frequency === f
                  ? { background: '#fff', color: accent, boxShadow: '0 1px 3px rgba(31,41,55,.08)' }
                  : { color: '#6B7280' }
              }
            >
              {FREQ_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Amount each time</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, ...SHEET_SERIF, fontSize: '19px' }}
          inputMode="decimal"
          placeholder="0.00"
          value={draft.amount || ''}
          onChange={(e) => set('amount', e.target.value)}
        />
      </div>

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>First contribution</label>
        <DateField
          className={SHEET_INPUT_CLS}
          style={SHEET_DATE_STYLE}
          value={draft.startDate || ''}
          onChange={(v) => set('startDate', v)}
        />
        <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
          Everything after this repeats on the same cadence.
        </p>
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Last contribution <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <DateField
          clearable
          className={SHEET_INPUT_CLS}
          style={SHEET_DATE_STYLE}
          value={draft.endDate || ''}
          onChange={(v) => set('endDate', v || null)}
        />
        <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
          Leave this blank and the contributions keep going, including into next
          year's window.
        </p>
      </div>

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Amount changes <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <p className="text-[12px] text-gray-500 mb-2.5 leading-snug">
          If the amount changes on a date, add it here instead of starting a
          second source. Everything from that date on uses the new amount.
        </p>

        {(draft.changes || []).length > 0 && (
          <div className="mb-2.5">
            {(draft.changes || []).map((c, i) => (
              <div
                key={c.effectiveDate + ':' + i}
                className="flex items-center gap-2.5 py-2.5 border-t theme-border-100"
              >
                <span className="flex-1 text-[13px]" style={{ color: '#1F2937' }}>
                  {formatCurrency(c.amount)} from {prettyDate(c.effectiveDate)}
                </span>
                <button
                  onClick={() => removeChange(i)}
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: track }}
                >
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#B4485F' }} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <DateField
            clearable
            wrapperClassName="flex-1 min-w-0"
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_DATE_STYLE, fontSize: '14px' }}
            value={changeDate}
            onChange={setChangeDate}
          />
          <input
            className={SHEET_INPUT_CLS + ' flex-1 min-w-0'}
            style={{ ...SHEET_INPUT_STYLE, fontSize: '14px', minHeight: '50px' }}
            inputMode="decimal"
            placeholder="New amount"
            value={changeAmount}
            onChange={(e) => setChangeAmount(e.target.value)}
          />
        </div>
        <button
          onClick={addChange}
          disabled={!changeDate || !(Number(changeAmount) > 0)}
          className="w-full rounded-full py-3 mt-2 text-[14px] font-semibold border theme-border-100"
          style={{ color: changeDate && Number(changeAmount) > 0 ? accent : '#C9C2C6' }}
        >
          Add this change
        </button>
      </div>

      <button
        disabled={!valid}
        onClick={() =>
          onSave({
            ...draft,
            amount: Number(draft.amount) || 0,
            startingAmount: Number(draft.startingAmount) || 0,
          })
        }
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {source ? 'Save changes' : 'Add source'}
      </button>

      {source && (
        <>
          {source.status === 'stopped' ? (
            <button
              onClick={() => onResume(source.id)}
              className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border theme-border-100"
              style={{ color: accent }}
            >
              Start it again
            </button>
          ) : (
            <button
              onClick={() => onStop(source.id)}
              className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border theme-border-100"
              style={{ color: '#6B7280' }}
            >
              Stop this source
            </button>
          )}
          <button
            onClick={() => setArmed(true)}
            className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border flex items-center justify-center gap-1.5"
            style={{ color: '#E11D48', borderColor: '#FBCFD8' }}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          {armed && (
            <ConfirmDialog
              title={'Delete ' + (draft.name.trim() || 'this source') + '?'}
              body="This removes the source and every scheduled contribution confirmed against it. Any one-offs you logged to it are kept, just no longer attached."
              confirmLabel="Delete"
              onCancel={() => setArmed(false)}
              onConfirm={() => { setArmed(false); onDelete(source.id); }}
            />
          )}
          <p className="text-[12px] text-gray-500 text-center mt-3 leading-snug">
            {'Stopping keeps everything already contributed and leaves the record alone. Deleting takes the source and its scheduled contributions with it.'}
          </p>
        </>
      )}
    </Sheet>
  );
};

/**
 * Choose which Wealth investment accounts feed this RRSP / TFSA with Budget
 * transfers. Accounts already linked to the other side are shown but locked,
 * so one transfer can never count twice.
 */
const LinkSheet = ({ account, investmentAccounts = [], linked = [], otherLinked = [], accent, track, onSave, onClose }) => {
  const [picked, setPicked] = useState(() => new Set(linked.map(String)));
  const other = new Set(otherLinked.map(String));
  const label = account === 'rrsp' ? 'RRSP' : 'TFSA';
  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };
  return (
    <Sheet title={'Link ' + label + ' accounts'} subtitle="Your investment accounts from Wealth" onClose={onClose} track={track}>
      {investmentAccounts.length === 0 ? (
        <p className="m-0 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
          You don't have any investment accounts in Wealth yet. Add one there first, then link it here.
        </p>
      ) : (
        <div>
          {investmentAccounts.map((a) => {
            const id = String(a.id);
            const locked = other.has(id);
            const on = picked.has(id);
            return (
              <button
                key={id}
                type="button"
                disabled={locked}
                onClick={() => toggle(id)}
                className="w-full flex items-center gap-3 py-3 text-left"
                style={{ borderTop: '1px solid #F3EDF1', opacity: locked ? 0.45 : 1 }}
              >
                <span
                  className="flex items-center justify-center flex-shrink-0 text-white text-[13px] font-bold"
                  style={{ width: '22px', height: '22px', borderRadius: '7px', border: '2px solid ' + (on ? accent : '#D9CCE3'), background: on ? accent : 'transparent' }}
                >
                  {on ? '✓' : ''}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14.5px] font-semibold text-gray-800 truncate">{a.name || 'Account'}</span>
                  {locked && (
                    <span className="block text-[12px]" style={{ color: '#8C8789' }}>
                      Linked to {account === 'rrsp' ? 'TFSA' : 'RRSP'}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          <p className="m-0 mt-3 text-[12.5px] leading-relaxed rounded-2xl p-3" style={{ background: '#FBF7F9', color: '#6b5f69' }}>
            Only link accounts you transfer into in your Budget. Contributions that come straight off your pay,
            like a workplace RRSP, never show up in Budget, so keep confirming those here by hand.
          </p>
        </div>
      )}
      <button
        type="button"
        onClick={() => onSave(Array.from(picked))}
        className="w-full mt-4 py-3.5 rounded-full text-[15px] font-bold text-white"
        style={{ background: accent }}
      >
        Save
      </button>
    </Sheet>
  );
};

const PlansTab = ({ plans, saveUserData, theme = 'pink', onShowHelp, onBack, investmentAccounts = [], transactions = [] }) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  // Purple (admin-only): lavender instead of hot pink and multicolour tiles.
  const purple = usePurple();
  const planLook = usePlan(); // plan_41: Money plan look
  // Matches the eyebrow colour used across Budget / Wealth / Calendar.
  const eyebrow = planLook ? '#7A5DD3' : purple ? '#7B5AA6' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const accent = planLook ? '#1F1E24' : purple ? '#8B5CE0' : isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE';
  const alt = planLook ? '#A58BE6' : purple ? '#C3AEEB' : isPink ? '#9B7FD0' : isWarm ? '#8B6B4D' : '#a855f7';
  const track = planLook ? '#F4F1FB' : purple ? '#F3ECFB' : isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
  const muted = '#a39fa0';
  // The confirm banner sits directly under the hero tiles and has to cut
  // through them, so it uses an attention colour rather than a tile colour -
  // everything in PLAN_TILE_PALETTES is picked to sit calmly beside its
  // neighbours, which is the opposite of what this needs to do.
  // Deliberately identical to the Home overdue banner, down to the shadow.
  // Both mean the same thing - something from the past that needs answering -
  // so they should look the same, and a user who has learned one has learned
  // the other. Fixed across themes: it is a state, not decoration.
  const alertTone = {
    bg: '#E11D48',
    shadow: '0 3px 12px rgba(225,29,72,.32)',
    line: 'rgba(255,255,255,.22)',
    chip: 'rgba(255,255,255,.22)',
    text: '#FFFFFF',
    soft: 'rgba(255,255,255,.85)',
  };

  // The wash an open group's contents sit on. Pale enough to read as "inside
  // this row" rather than as another coloured band competing with the tiles.
  /**
   * A pale wash of a colour, for the region under an open group. Derived at
   * run time rather than stored as a second palette, so it tracks whatever
   * SOURCE_COLORS gives a source on the current theme and can never drift out
   * of step with the dot and the name it sits under.
   */
  const tintOf = (hex, alpha) => {
    const h = String(hex || '').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    if (!full || Number.isNaN(n)) return 'rgba(0,0,0,0.04)';
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
  };

  // The hero's scheduled segment carries the verdict. Green is tuned per theme
  // so it sits against each background; amber is shared, like the status pill,
  // because falling short means the same thing everywhere.
  const planGreen = planLook ? '#8FD3AC' : isPink ? '#7FB08C' : isWarm ? '#93AF72' : '#5FA890';
  const planAmber = planLook ? '#F2AFC8' : '#E0A951';

  const SOURCE_COLORS = planLook ? ['#5C86C9', '#8B6FD9', '#3F9E6B'] : [alt, accent, isPink ? '#5C9A4A' : isWarm ? '#5D6B4D' : '#0EA5E9'];
  const tilePalette = planLook ? PLAN_LOOK_TILES : PLAN_TILE_PALETTES[purple ? 'purple' : isPink ? 'pink' : isWarm ? 'warm' : 'blue'];

  const cardCls = planLook ? "bg-white rounded-[28px]" : "bg-white rounded-2xl border theme-border-100 shadow-sm";
  const labelStyle = planLook ? { color: '#2B2C31', fontWeight: 500 } : { color: '#1F2937', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" };
  const serif = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };

  const [local, setLocal] = useState(() => normalizePlans(plans));
  useEffect(() => { setLocal(normalizePlans(plans)); }, [plans]);

  const persist = (next) => {
    setLocal(next);
    saveUserData({ plans: next });
  };

  const todayKey = useMemo(() => todayLocalKey(), []);
  const [taxYear, setTaxYear] = useState(() => new Date().getFullYear());
  const [view, setView] = useState('overview');
  const [showInfo, setShowInfo] = useState(false);

  // Sheets
  const [targetSheet, setTargetSheet] = useState(null);   // { account }
  const [sourceSheet, setSourceSheet] = useState(null);   // { account, source|null }
  const [oneOffSheet, setOneOffSheet] = useState(null);   // { account }
  const [confirmSheet, setConfirmSheet] = useState(null); // an occurrence row
  // 'all' | 'rrsp' | 'tfsa'. Both accounts together is the view that is new
  // here, so it is the one the tab opens on.
  const [schedAccount, setSchedAccount] = useState('all');
  const [openOneOffs, setOpenOneOffs] = useState({});
  const [openConfirmed, setOpenConfirmed] = useState({});
  // Holds the id of the one-off awaiting a second tap before it is removed.
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Which account's detail page is open. null = the Overview / Schedule pair.
  const [detailAccount, setDetailAccount] = useState(null);

  const rrspWindow = useMemo(() => getRrspWindow(taxYear), [taxYear]);

  // Contribution room is a fact about one specific year - your RRSP limit in
  // 2027 has nothing to do with 2026's - so it is stored per year and never
  // carried forward. A missing year stays missing rather than borrowing.
  const roomFor = (key, year) => {
    const cfg = local.accounts[key] || {};
    const byYear = cfg.roomByYear || {};
    if (year in byYear) return byYear[year];
    // Room entered before this was year-aware belongs to whichever year the
    // app was showing at the time, which can only have been the current one.
    if (cfg.roomAvailable != null && year === new Date().getFullYear()) {
      return cfg.roomAvailable;
    }
    return null;
  };

  // A target is an intention rather than a fact about a year, so it does carry:
  // last year's figure stands until you set a different one.
  const targetFor = (key, year) => {
    const cfg = local.accounts[key] || {};
    const byYear = cfg.targetsByYear || {};
    if (year in byYear) return byYear[year] || 0;
    return cfg.target || 0;
  };

  // Like room, this is a fact about one year and never carries forward.
  const openingFor = (key, year) => {
    const cfg = local.accounts[key] || {};
    const byYear = cfg.openingByYear || {};
    return year in byYear ? (byYear[year] || 0) : 0;
  };

  const accountInUse = (key) =>
    (local.accounts[key] || {}).configured || local.sources.some((x) => x.account === key);

  const summaries = useMemo(() => {
    const out = {};
    for (const a of ACCOUNTS) {
      out[a.key] = summarize({
        account: a.key,
        taxYear,
        sources: local.sources,
        contributions: local.contributions,
        target: targetFor(a.key, taxYear),
        roomAvailable: roomFor(a.key, taxYear),
        opening: openingFor(a.key, taxYear),
        todayKey,
      });
    }
    return out;
  }, [local, taxYear, todayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const combined = useMemo(() => {
    const r = summaries.rrsp, t = summaries.tfsa;
    // Only count accounts actually in use, and refuse to show a room total at
    // all when one of them is missing its figure - a partial sum reads as a
    // complete one and would quietly understate how much room is left.
    const inUse = ACCOUNTS.filter((a) => accountInUse(a.key));
    const known = inUse.filter((a) => summaries[a.key].roomLeft != null);
    return {
      confirmed: r.confirmed + t.confirmed,
      target: r.target + t.target,
      targetLeft: r.targetLeft + t.targetLeft,
      projected: r.projected + t.projected,
      roomLeft: known.reduce((n, a) => n + summaries[a.key].roomLeft, 0),
      roomComplete: inUse.length > 0 && known.length === inUse.length,
    };
  }, [summaries, local]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything whose date has passed with no decision on it yet, both
  // accounts together, oldest first. Built off the summaries rather than
  // calling pendingOccurrences again so it cannot disagree with the totals.
  const dueRows = useMemo(() => {
    const out = [];
    for (const a of ACCOUNTS) {
      for (const o of summaries[a.key].occurrences) {
        if (o.state === 'due') out.push(o);
      }
    }
    out.sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : 0));
    return out;
  }, [summaries]);

  // Budget transfers into linked accounts, as suggested contributions.
  const suggestions = useMemo(
    () => findTransferSuggestions({ plans: local, transactions, taxYear, todayKey }),
    [local, transactions, taxYear, todayKey]
  );
  const [linkSheet, setLinkSheet] = useState(null);        // { account }
  const acctName = (id) => {
    const a = (investmentAccounts || []).find((x) => String(x.id) === String(id));
    return a ? (a.name || 'Account') : 'Account';
  };

  const saveLinks = (account, ids) => {
    persist({
      ...local,
      // First link ever: only transfers from today on are looked at.
      transferTrackingSince: local.transferTrackingSince || (ids.length ? todayKey : undefined),
      accounts: {
        ...local.accounts,
        [account]: { ...(local.accounts[account] || {}), linkedAccountIds: ids },
      },
    });
    setLinkSheet(null);
  };

  const acceptMatch = (sug, occ) => {
    persist({
      ...local,
      contributions: [
        ...local.contributions.filter((c) => c.occurrenceId !== occ.occurrenceId),
        newContribution({
          sourceId: occ.sourceId,
          occurrenceId: occ.occurrenceId,
          account: occ.account,
          amount: sug.transfer.amount,
          date: occ.date,
          taxYear,
          status: 'confirmed',
          type: 'scheduled',
          transactionId: sug.transfer.id,
        }),
      ],
    });
  };

  const acceptOneOff = (sug) => {
    persist({
      ...local,
      contributions: [
        ...local.contributions,
        newContribution({
          account: sug.account,
          amount: sug.transfer.amount,
          date: sug.transfer.date,
          taxYear,
          status: 'confirmed',
          type: 'oneoff',
          note: 'From a Budget transfer',
          transactionId: sug.transfer.id,
        }),
      ],
    });
  };

  // "Not this one": remembered, so the pair is never suggested again. The
  // scheduled slot stays pending; the transfer returns on its own row.
  const rejectMatch = (item) => {
    persist({
      ...local,
      rejectedMatches: Array.from(new Set([
        ...(local.rejectedMatches || []),
        item.transfer.id + '|' + item.occ.occurrenceId,
      ])),
    });
  };

  // The To review row whose sheet is open.
  const [reviewOpen, setReviewOpen] = useState(null);

  // One list for the card - see buildReviewItems.
  const reviewItems = useMemo(
    () => buildReviewItems({ plans: local, transactions, taxYear, todayKey, dueOccurrences: dueRows }),
    [local, transactions, taxYear, todayKey, dueRows]
  );

  const ignoreTransfers = (ids) => {
    persist({
      ...local,
      ignoredTransferIds: Array.from(new Set([...(local.ignoredTransferIds || []), ...ids.map(String)])),
    });
  };

  const isSetUp = local.sources.length > 0 ||
    accountInUse('rrsp') || accountInUse('tfsa');

  /* ---------------- mutations ---------------- */

  const saveTarget = (account, target, roomAvailable, opening) => {
    persist({
      ...local,
      accounts: {
        ...local.accounts,
        [account]: {
          ...local.accounts[account],
          // Standing default, used for any year without its own figure.
          target: Number(target) || 0,
          targetsByYear: {
            ...((local.accounts[account] || {}).targetsByYear || {}),
            [taxYear]: Number(target) || 0,
          },
          roomByYear: {
            ...((local.accounts[account] || {}).roomByYear || {}),
            [taxYear]: roomAvailable === '' || roomAvailable == null
              ? null
              : Number(roomAvailable),
          },
          openingByYear: {
            ...((local.accounts[account] || {}).openingByYear || {}),
            [taxYear]: Number(opening) || 0,
          },
          // Records that we asked, so someone who genuinely wants no target
          // and no room is not sent back here every time they add a source.
          configured: true,
        },
      },
    });
    const chain = targetSheet && targetSheet.thenAddSource;
    setTargetSheet(null);
    if (chain) setSourceSheet({ account, source: null });
  };

  // An account has a baseline once we've asked for it, whichever account the
  // user happened to set up first.
  const needsBaseline = (key) => {
    const cfg = local.accounts[key] || {};
    return !cfg.configured && !(cfg.target > 0) && cfg.roomAvailable == null;
  };

  const logOneOff = (entry) => {
    const edited = entry.id
      ? local.contributions.map((c) =>
          c.id === entry.id
            ? {
                ...c,
                amount: entry.amount,
                date: entry.date,
                taxYear: entry.taxYear,
                sourceId: entry.sourceId || null,
                note: entry.note,
              }
            : c
        )
      : [
          ...local.contributions,
          newContribution({
            account: entry.account,
            amount: entry.amount,
            date: entry.date,
            taxYear: entry.taxYear,
            sourceId: entry.sourceId || null,
            note: entry.note,
            status: 'confirmed',
            type: 'oneoff',
          }),
        ];
    persist({ ...local, contributions: edited });
    setOneOffSheet(null);
  };

  /**
   * Confirming and skipping both write a contribution row carrying the
   * occurrenceId, which is what buildOccurrences matches on. The filter first
   * means a second decision on the same slot replaces the first rather than
   * stacking a duplicate.
   */
  const confirmOccurrence = (occ, amount) => {
    persist({
      ...local,
      contributions: [
        ...local.contributions.filter((c) => c.occurrenceId !== occ.occurrenceId),
        newContribution({
          sourceId: occ.sourceId,
          occurrenceId: occ.occurrenceId,
          account: occ.account,
          amount: Number(amount) || 0,
          date: occ.date,
          taxYear,
          status: 'confirmed',
          type: 'scheduled',
        }),
      ],
    });
    setConfirmSheet(null);
  };

  const skipOccurrence = (occ) => {
    persist({
      ...local,
      contributions: [
        ...local.contributions.filter((c) => c.occurrenceId !== occ.occurrenceId),
        newContribution({
          sourceId: occ.sourceId,
          occurrenceId: occ.occurrenceId,
          account: occ.account,
          amount: 0,
          date: occ.date,
          taxYear,
          status: 'skipped',
          type: 'scheduled',
        }),
      ],
    });
    setConfirmSheet(null);
  };

  /**
   * Puts a decided slot back to undecided. Safe to delete outright because the
   * occurrence itself is generated from the source, not stored - so the slot
   * reappears as due the moment its row is gone.
   */
  const unconfirmOccurrence = (occ) => {
    persist({
      ...local,
      contributions: local.contributions.filter((c) => c.occurrenceId !== occ.occurrenceId),
    });
    setConfirmSheet(null);
  };

  const removeContribution = (id) => {
    persist({ ...local, contributions: local.contributions.filter((c) => c.id !== id) });
  };

  const saveSource = (draft) => {
    const exists = local.sources.some((s) => s.id === draft.id);
    persist({
      ...local,
      sources: exists
        ? local.sources.map((s) => (s.id === draft.id ? draft : s))
        : [...local.sources, draft],
    });
    setSourceSheet(null);
  };

  const stopSource = (id) => {
    persist({
      ...local,
      sources: local.sources.map((s) =>
        s.id === id ? { ...s, status: 'stopped', stoppedDate: todayKey } : s
      ),
    });
    setSourceSheet(null);
  };

  const resumeSource = (id) => {
    persist({
      ...local,
      sources: local.sources.map((s) =>
        s.id === id ? { ...s, status: 'active', stoppedDate: null } : s
      ),
    });
    setSourceSheet(null);
  };

  /**
   * Deleting a source drops its SCHEDULED rows - they describe slots that no
   * longer exist, and leaving them would inflate the confirmed total with no
   * visible cause. One-offs attached to it are detached instead: that money
   * genuinely went in, and removing a schedule should not erase it.
   */
  const deleteSource = (id) => {
    persist({
      ...local,
      sources: local.sources.filter((s) => s.id !== id),
      contributions: local.contributions
        .filter((c) => !(c.sourceId === id && c.occurrenceId))
        .map((c) => (c.sourceId === id ? { ...c, sourceId: null } : c)),
    });
    setSourceSheet(null);
  };

  /* ---------------- pieces ---------------- */

  const Eyebrow = ({ children, className = '' }) => (planLook ? (
    <p className={'px-1 ' + className} style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31', margin: '0 0 10px' }}>{children}</p>
  ) : (
    <p
      className={"text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 " + className}
      style={{ color: eyebrow }}
    >
      {children}
    </p>
  ));

  const Tile = ({ emoji, label, value, tone }) => {
    const t = tilePalette[tone];
    return (
      <div className="rounded-[22px]" style={{ background: t.bg, padding: '12px 12px 11px' }}>
        <div
          className="rounded-lg flex items-center justify-center"
          style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '10px' }}
        >
          {emoji}
        </div>
        <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.label, marginBottom: '2px' }}>
          {label}
        </span>
        <p style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: t.value }}>
          {value}
        </p>
      </div>
    );
  };

  const SourceRow = ({ source, summary, index }) => {
    const dotColor = source.status === 'stopped'
      ? '#C9C2C6'
      : SOURCE_COLORS[index % SOURCE_COLORS.length];
    const per = (summary.perSource || []).find((p) => p.id === source.id) || {};
    const stopped = source.status === 'stopped';

    return (
      <button
        onClick={() => setSourceSheet({ account: source.account, source })}
        className="w-full flex items-center gap-3 py-3 text-left border-t theme-border-100"
        style={{ opacity: stopped ? 0.55 : 1 }}
      >
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: dotColor }}
        />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-[14px] truncate" style={labelStyle}>{source.name}</span>
            {stopped && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background: '#F2EEF0', color: '#6B7280' }}
              >
                Stopped
              </span>
            )}
            {!stopped && per.dueCount > 0 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background: accent, color: '#fff' }}
              >
                {per.dueCount} to confirm
              </span>
            )}
          </span>
          <span className="block text-[12px] text-gray-500 mt-0.5 truncate">
            {formatCurrency(source.amount)} {FREQ_LABELS[source.frequency].toLowerCase()}
            {(() => {
              if (stopped) return ' \u00b7 stopped ' + prettyShort(source.stoppedDate);
              // A dated change is invisible on this row otherwise - you would
              // have to reopen the sheet to remember it exists.
              const next = (source.changes || [])
                .filter((c) => c.effectiveDate > todayKey)
                .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1))[0];
              if (next) {
                return ' \u00b7 ' + formatCurrency(next.amount) + ' from ' + prettyShort(next.effectiveDate);
              }
              return per.nextDate ? ' \u00b7 next ' + prettyShort(per.nextDate) : '';
            })()}
          </span>
        </span>
        <span className="text-[15px] flex-shrink-0" style={{ ...serif, color: dotColor }}>
          {formatCurrency(per.confirmed || 0)}
        </span>
      </button>
    );
  };

  const AccountCard = ({ account }) => {
    const meta = ACCOUNTS.find((a) => a.key === account.key ? true : false) || account;
    const s = summaries[account.key];
    const cfg = local.accounts[account.key];
    const mine = local.sources.filter((x) => x.account === account.key);

    return (
      <div className={cardCls + " mb-3 overflow-hidden"}>
        {(() => {
          // The window dates are useful whether or not there's a goal, so they
          // show as soon as the account has anything in it.
          const hasData = mine.length > 0 || cfg.configured;
          const range = account.key === 'rrsp'
            ? prettyDate(s.window.start) + ' \u2013 ' + prettyDate(s.window.end)
            : 'Jan 1 \u2013 Dec 31, ' + taxYear;
          // Both bar segments are drawn against the TARGET and capped at the
          // track, so a plan of 141% and one of exactly 100% look the same
          // here. The percentage below is what tells them apart.
          const confirmedW = s.target > 0
            ? Math.max(0, Math.min(100, (s.confirmed / s.target) * 100))
            : 0;
          const scheduledW = s.target > 0
            ? Math.max(0, Math.min(100 - confirmedW, ((s.projected - s.confirmed) / s.target) * 100))
            : 0;
          const planPct = s.target > 0 ? Math.round((s.projected / s.target) * 100) : 0;

          return (
            <div className="p-4" style={{ background: tilePalette[0].bg }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.75)' }}
                >
                  {account.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="m-0 text-[12px]" style={{ color: tilePalette[0].label }}>
                    {s.target > 0 ? 'Confirmed this year' : 'Contributed this year'}
                  </p>
                  <p className="m-0" style={{ fontSize: '25px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15, color: tilePalette[0].value }}>
                    {formatCurrency(s.confirmed)}
                  </p>
                </div>
                {s.target > 0 ? (
                  <div className="text-right flex-shrink-0">
                    <p className="m-0 text-[12px]" style={{ color: tilePalette[0].label }}>Target</p>
                    <p className="m-0 text-[16px]" style={{ ...serif, color: tilePalette[0].value }}>
                      {formatCurrency(s.target)}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setTargetSheet({ account: account.key })}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.75)', color: tilePalette[0].value }}
                  >
                    <Target className="w-4 h-4" />
                  </button>
                )}
              </div>

              <p className="m-0 mt-2.5 text-[11.5px]" style={{ color: tilePalette[0].label, opacity: 0.75 }}>
                {hasData ? range : 'Track your contributions for the year'}
              </p>

              {s.target > 0 && (
                <>
                  <div
                    className="flex h-2 rounded-full overflow-hidden mt-1.5"
                    style={{ background: 'rgba(255,255,255,0.7)' }}
                  >
                    <div style={{ width: confirmedW + '%', backgroundColor: tilePalette[0].value, transition: 'width 0.3s' }} />
                    <div style={{ width: scheduledW + '%', backgroundColor: tilePalette[0].value, opacity: 0.4, transition: 'width 0.3s' }} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[12px]" style={{ color: tilePalette[0].label }}>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tilePalette[0].value }} />
                      {s.targetPct}% confirmed
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tilePalette[0].value, opacity: 0.4 }} />
                      {planPct}% planned
                    </span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        <div className="p-4 pt-2">
        <div className="mt-1">
          {mine.map((src, i) => (
            <SourceRow key={src.id} source={src} summary={s} index={i} />
          ))}
          {(() => {
            // Confirmed scheduled occurrences, newest first. One-offs are a
            // separate list below because they behave differently: a one-off
            // can be deleted outright, this cannot - the source still says
            // money was due that day.
            const done = s.occurrences
              .filter((o) => o.state === 'confirmed')
              .sort((a, b) => (a.date < b.date ? 1 : -1));
            if (done.length === 0) return null;
            const total = done.reduce((n, o) => n + (Number(o.amount) || 0), 0);
            const open = !!openConfirmed[account.key];

            return (
              <>
                <button
                  onClick={() => {
                    setConfirmRemove(null);
                    setOpenConfirmed((o) => ({ ...o, [account.key]: !o[account.key] }));
                  }}
                  className="w-full flex items-center gap-3 py-3 border-t theme-border-100 text-left"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: accent }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px]" style={labelStyle}>
                      {done.length} confirmed
                    </span>
                  </span>
                  <span className="text-[15px] flex-shrink-0" style={{ ...serif, color: accent }}>
                    {formatCurrency(total)}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 text-gray-400 flex-shrink-0"
                    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                  />
                </button>

                {open && done.map((o) => (
                  <button
                    key={o.occurrenceId}
                    onClick={() => setConfirmSheet(o)}
                    className="w-full flex items-center gap-3 py-2.5 pl-5 border-t theme-border-100 text-left"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13.5px] truncate" style={labelStyle}>
                        {o.sourceName}
                      </span>
                      <span className="block text-[12px] text-gray-500 mt-0.5">
                        {prettyDate(o.date)}
                        {Math.abs(Number(o.amount) - Number(o.plannedAmount)) > 0.005
                          ? ' \u00b7 scheduled ' + formatCurrency(o.plannedAmount)
                          : ''}
                      </span>
                    </span>
                    <span className="text-[14px] flex-shrink-0" style={{ ...serif, color: accent }}>
                      {formatCurrency(o.amount)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </>
            );
          })()}
          {(() => {
            const oneOffs = local.contributions
              .filter((c) => c.account === account.key && !c.occurrenceId
                && (c.taxYear != null ? c.taxYear : taxYear) === taxYear)
              .sort((a, b) => (a.date < b.date ? 1 : -1));
            if (oneOffs.length === 0) return null;
            const total = oneOffs.reduce((n, c) => n + (Number(c.amount) || 0), 0);
            const open = !!openOneOffs[account.key];

            return (
              <>
                <button
                  onClick={() => {
                    setConfirmRemove(null);
                    setOpenOneOffs((o) => ({ ...o, [account.key]: !o[account.key] }));
                  }}
                  className="w-full flex items-center gap-3 py-3 border-t theme-border-100 text-left"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: accent, opacity: 0.4 }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px]" style={labelStyle}>
                      {oneOffs.length} one-off{oneOffs.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="text-[15px] flex-shrink-0" style={{ ...serif, color: accent }}>
                    {formatCurrency(total)}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 text-gray-400 flex-shrink-0"
                    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                  />
                </button>

                {open && oneOffs.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 py-2.5 pl-5 border-t theme-border-100">
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13.5px] truncate" style={labelStyle}>
                        {c.note ? c.note : 'One-off'}
                      </span>
                      <span className="block text-[12px] text-gray-500 mt-0.5">
                        {prettyDate(c.date)}
                        {c.taxYear != null && c.taxYear !== Number(c.date.slice(0, 4))
                          ? ' \u00b7 counted to ' + c.taxYear
                          : ''}
                      </span>
                    </span>

                    {confirmRemove === c.id ? (
                      <>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="text-[12px] font-semibold px-2.5 py-1.5 rounded-full flex-shrink-0"
                          style={{ background: track, color: '#6B7280' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => { removeContribution(c.id); setConfirmRemove(null); }}
                          className="text-[12px] font-bold px-2.5 py-1.5 rounded-full flex-shrink-0 text-white"
                          style={{ background: tilePalette[0].value }}
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-[14px] flex-shrink-0" style={{ ...serif, color: accent }}>
                          {formatCurrency(c.amount)}
                        </span>
                        <button
                          onClick={() => setConfirmRemove(c.id)}
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: track }}
                        >
                          <Trash2 className="w-3.5 h-3.5" style={{ color: tilePalette[0].value }} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </>
            );
          })()}
        </div>

        {s.target > 0 && (() => {
          // Everything the plan will have put in by the time the window shuts:
          // already confirmed, plus what is still due or scheduled inside it.
          const gap = s.target - s.projected;
          const reaches = gap <= 0;
          // Round up so "1 month left" never reads as a fraction of a payment.
          const monthsLeft = Math.max(1, Math.ceil(s.daysLeft / 30.44));

          let verdict;
          if (s.daysLeft <= 0) {
            verdict = reaches
              ? 'The window has closed and you met your target.'
              : 'The window has closed ' + formatCurrency(gap) + ' short of your target.';
          } else if (gap === 0) {
            verdict = 'Lands exactly on your target.';
          } else if (reaches) {
            verdict = formatCurrency(Math.abs(gap)) + ' to spare.';
          } else {
            verdict = formatCurrency(gap) + ' short \u00b7 about ' +
              formatCurrency(gap / monthsLeft) + ' a month more closes it.';
          }

          const tone = reaches ? tilePalette[2] : tilePalette[0];

          return (
            <div className="rounded-2xl p-3 mt-3" style={{ background: tone.bg }}>
              <div className="flex items-baseline justify-between gap-3">
                <p className="m-0 text-[13px]" style={{ color: tone.label }}>
                  Your plan by {prettyShort(s.window.end)}
                </p>
                <p className="m-0 text-[19px] flex-shrink-0" style={{ ...serif, color: tone.value }}>
                  {formatCurrency(s.projected)}
                </p>
              </div>
              <p className="m-0 mt-1 text-[12px] leading-snug" style={{ color: tone.label }}>
                {verdict}
              </p>
            </div>
          );
        })()}

        {s.roomAvailable != null ? (
          <>
            <div className="flex gap-2 mt-3">
              <div className="flex-1 min-w-0 rounded-xl px-2.5 py-2" style={{ background: track }}>
                <p className="m-0 text-[11px] text-gray-500 mb-0.5">Room</p>
                <p className="m-0 text-[14px] truncate" style={{ ...serif, color: '#1F2937' }}>
                  {formatCurrency(s.roomAvailable)}
                </p>
              </div>
              <div className="flex-1 min-w-0 rounded-xl px-2.5 py-2" style={{ background: track }}>
                <p className="m-0 text-[11px] text-gray-500 mb-0.5">Left</p>
                <p className="m-0 text-[14px] truncate" style={{ ...serif, color: '#1F2937' }}>
                  {formatCurrency(s.roomLeft)}
                </p>
              </div>
              <div
                className="min-w-0 rounded-xl px-2.5 py-2"
                style={{ flex: '1.3', background: s.roomAfterPlan < 0 ? '#FEF2F2' : tilePalette[2].bg }}
              >
                <p className="m-0 text-[11px] mb-0.5" style={{ color: s.roomAfterPlan < 0 ? '#B91C1C' : tilePalette[2].label }}>
                  After your plan
                </p>
                <p className="m-0 text-[14px] truncate" style={{ ...serif, color: s.roomAfterPlan < 0 ? '#B91C1C' : tilePalette[2].value }}>
                  {formatCurrency(s.roomAfterPlan)}
                </p>
              </div>
            </div>
            {s.roomAfterPlan < 0 && (
              <p className="m-0 mt-2.5 text-[12px] leading-snug px-3 py-2.5 rounded-xl" style={{ background: '#FEF2F2', color: '#B91C1C' }}>
                Your scheduled contributions go {formatCurrency(Math.abs(s.roomAfterPlan))} past
                your {taxYear} room. The CRA charges 1% a month on the excess, so
                it is worth trimming or stopping something before then.
              </p>
            )}
          </>
        ) : (mine.length > 0 || cfg.configured) ? (
          <button
            onClick={() => setTargetSheet({ account: account.key })}
            className="w-full flex items-start gap-2.5 mt-3 pt-3 border-t theme-border-100 text-left"
          >
            <span className="flex-1 min-w-0">
              <span className="block text-[13px]" style={{ color: tilePalette[0].value, fontWeight: 600 }}>
                Add your {taxYear} contribution room
              </span>
              <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">
                {account.key === 'rrsp'
                  ? 'Room left stays blank until you enter it. Your new limit is on the notice of assessment for last year.'
                  : 'Room left stays blank until you enter it. Your new limit is in CRA My Account.'}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" style={{ marginTop: '2px' }} />
          </button>
        ) : null}

        <div className="flex gap-2 mt-4 pt-3.5 border-t theme-border-100">
          <button
            onClick={() =>
              needsBaseline(account.key)
                ? setTargetSheet({ account: account.key, thenAddSource: true })
                : setSourceSheet({ account: account.key, source: null })
            }
            className="rounded-full text-[13.5px] font-semibold flex items-center justify-center gap-1 py-2.5"
            style={{ flex: '1.35', background: track, color: accent }}
          >
            <Plus className="w-4 h-4" /> Add a source
          </button>
          <button
            onClick={() => setTargetSheet({ account: account.key })}
            className="rounded-full text-[13.5px] font-semibold flex items-center justify-center py-2.5 border theme-border-100"
            style={{ flex: '1.2', color: accent }}
          >
            Goal &amp; room
          </button>
          <button
            onClick={() => setOneOffSheet({ account: account.key })}
            className="flex-1 rounded-full text-[13.5px] font-semibold flex items-center justify-center py-2.5 border theme-border-100"
            style={{ color: accent }}
          >
            One-off
          </button>
        </div>
        </div>
      </div>
    );
  };

  /* ---------------- render ---------------- */

  const pace = paceStatus(summaries.rrsp);
  // Same three verdict palettes BudgetHero uses for Over pace / Slow down / Ahead.
  const paceTone =
    pace.key === 'behind'
      ? { bg: '#FEF2F2', fg: '#B91C1C', dot: '#DC2626' }
      : pace.key === 'slightly'
      ? { bg: '#FFFBEB', fg: '#B45309', dot: '#D97706' }
      : { bg: '#F0FDF4', fg: '#15803D', dot: '#16A34A' };

  /* ---------------- account detail page ---------------- */

  const renderAccountDetail = () => {
    const key = detailAccount;
    const meta = ACCOUNTS.find((a) => a.key === key) || ACCOUNTS[0];
    const s = summaries[key];
    // Account colour is a palette SLOT, not a hex, so it remaps per theme.
    const tile = tilePalette[key === 'rrsp' ? 0 : 1];
    const win = s.window;
    const mySources = local.sources.filter((x) => x.account === key);
    const opening = openingFor(key, taxYear);

    const hasTarget = s.target > 0;
    const surplus = s.projected - s.target;
    const shortBy = Math.max(0, -surplus);
    const monthsLeft = Math.max(1, Math.round(s.daysLeft / 30.44));
    const perMonth = shortBy > 0 ? Math.ceil(shortBy / monthsLeft) : 0;
    const onTrack = hasTarget && surplus >= 0;

    // Status colours are deliberately FIXED across all three themes. A status
    // is a state, not decoration - if it remapped per theme it would collide
    // with an account colour in one of them.
    const tone = !hasTarget
      ? { bg: '#FFFFFF', fg: '#1F2937', body: '#8C8789', dot: null }
      : onTrack
        ? { bg: '#EEF5E9', fg: '#2F6B44', body: '#41765A', dot: '#2F6B44' }
        : { bg: '#FDF0E2', fg: '#8A5A1E', body: '#96702F', dot: '#C98A2E' };

    // The bar runs 0 to target and nothing else. A bar whose ends mean two
    // different things depending on your numbers cannot be read at a glance.
    const scale = Math.max(s.target, 1);
    const pct = (n) => Math.max(0, Math.min(100, (n / scale) * 100));

    // Confirmed money grouped by the source it came through, because a flat
    // list grows without limit once someone logs a few one-offs. One-offs fold
    // into their source; anything unattached gets a group of its own. Starting
    // balances and the opening figure are real money in and belong here too,
    // or the groups would not add up to the number in the hero.
    const claimedYear = (c) => (c.taxYear != null ? c.taxYear : taxYear);
    const groups = [];

    mySources.forEach((src, i) => {
      const rows = [];
      for (const o of s.occurrences) {
        if (o.sourceId !== src.id) continue;
        if (o.state !== 'confirmed' && o.state !== 'skipped') continue;
        rows.push({
          id: o.occurrenceId,
          label: o.state === 'skipped' ? 'Skipped' : 'Scheduled contribution',
          date: o.date,
          amount: o.state === 'skipped' ? 0 : o.amount,
          skipped: o.state === 'skipped',
          occ: o,
        });
      }
      const start = Number(src.startingAmount) || 0;
      if (start && src.startDate && src.startDate >= win.start && src.startDate <= win.end) {
        rows.push({
          id: 'start_' + src.id,
          label: 'Before tracking began',
          date: src.startDate,
          amount: start,
          // Edited on the source itself, so that is where the row leads.
          srcId: src.id,
        });
      }
      for (const c of local.contributions) {
        if (c.account !== key || c.occurrenceId || c.status === 'skipped') continue;
        if (c.sourceId !== src.id || claimedYear(c) !== taxYear) continue;
        // Only a one-off can be removed. A scheduled slot that was confirmed or
        // skipped is undone from its sheet instead, not deleted from here.
        rows.push({
          id: c.id,
          label: c.note || 'One-off',
          date: c.date,
          amount: Number(c.amount) || 0,
          oneoff: true,
          contrib: c,
        });
      }
      if (!rows.length) return;
      rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
      const offs = rows.filter((r) => r.oneoff).length;
      groups.push({
        key: src.id,
        name: src.name,
        color: SOURCE_COLORS[i % SOURCE_COLORS.length],
        nameColor: SOURCE_COLORS[i % SOURCE_COLORS.length],
        rows,
        total: rows.reduce((n, r) => n + (r.skipped ? 0 : r.amount), 0),
        meta: rows.length + (rows.length === 1 ? ' entry' : ' entries') +
          (offs ? ' \u00b7 ' + offs + (offs > 1 ? ' one-offs' : ' one-off') : ''),
      });
    });

    const loose = [];
    for (const c of local.contributions) {
      if (c.account !== key || c.occurrenceId || c.status === 'skipped') continue;
      if (c.sourceId || claimedYear(c) !== taxYear) continue;
      loose.push({
        id: c.id,
        label: c.note || 'One-off',
        date: c.date,
        amount: Number(c.amount) || 0,
        oneoff: true,
        contrib: c,
      });
    }
    if (opening > 0) {
      loose.push({
        id: 'opening',
        label: 'Already contributed this year',
        date: win.start,
        amount: opening,
        // Lives on goal & room, which is where this row goes.
        openingRow: true,
      });
    }
    if (loose.length) {
      loose.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
      groups.push({
        key: '__loose',
        name: 'Not from a source',
        color: '#C9C2C6',
        // Its dot is deliberately pale, but the same grey as a name would read
        // as disabled rather than as a different kind of group.
        nameColor: '#6B7280',
        rows: loose,
        total: loose.reduce((n, r) => n + r.amount, 0),
        meta: loose.length + (loose.length === 1 ? ' entry' : ' entries'),
      });
    }

    const confirmedCount = groups.reduce((n, g) => n + g.rows.length, 0);

    // overflow-hidden matters: an open group's tinted region bleeds to the card
    // edges with a negative margin, and would square off the bottom corners.
    // plan_41: the Money plan look of the top of an account's page
    const plLook = key === 'rrsp' ? { wash: '#DFE9F9', ink: '#3C6DB5', line: '#5C86C9' } : { wash: '#DFF2E7', ink: '#2F8756', line: '#4FA877' };
    const PL_SOFT = '#8B879A'; // planner_restyle
    // dark_09: a month-by-month chart like the Wealth graphs. The solid line
    // is what's gone in, the dashed line is what your schedule adds.
    const plChart = (() => {
      if (!hasTarget) return [];
      const pad = (n) => String(n).padStart(2, '0');
      const [sy, sm] = String(win.start).split('-').map(Number);
      const [ey, em] = String(win.end).split('-').map(Number);
      const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const inRows = groups.flatMap((g) => g.rows).filter((r) => !r.skipped);
      const ahead = (s.occurrences || []).filter((o) => o.state === 'due' || o.state === 'upcoming');
      const nowMonth = String(todayKey).slice(0, 7);
      const out = [];
      let y = sy; let m = sm;
      while (y < ey || (y === ey && m <= em)) {
        const mk = y + '-' + pad(m);
        const endKey = mk + '-' + pad(new Date(y, m, 0).getDate());
        const cut = mk === nowMonth ? todayKey : endKey;
        const actual = inRows.filter((r) => r.date <= cut).reduce((n, r) => n + (Number(r.amount) || 0), 0);
        const point = { m: MON[m - 1], a: null, p: null };
        if (mk <= nowMonth) point.a = actual;
        if (mk === nowMonth) point.p = actual;
        if (mk > nowMonth) point.p = s.confirmed + ahead.filter((o) => o.date <= endKey).reduce((n, o) => n + (Number(o.plannedAmount) || 0), 0);
        out.push(point);
        m += 1; if (m > 12) { m = 1; y += 1; }
      }
      // A window that hasn't started yet: the dashed line starts at the first month.
      if (!out.some((d) => d.a != null) && out.length) out[0].p = out[0].p == null ? s.confirmed : out[0].p;
      return out;
    })();
    const plSym = getCurrentCurrency().symbol;
    // Round steps for the left axis, like $0 / $5k / $10k / $15k.
    const plTop = Math.max(s.target, ...plChart.map((d) => Math.max(d.a || 0, d.p || 0)), 1) * 1.05;
    const plQ = Math.pow(10, Math.floor(Math.log10(plTop))) / 2;
    const plStep = plQ * Math.ceil(Math.ceil(plTop / plQ) / 4);
    const plTicks = Array.from({ length: Math.ceil(plTop / plStep) + 1 }, (_, i) => i * plStep);
    const plTick = (v) => (Math.abs(v) >= 1000 ? plSym + (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'k' : plSym + Math.round(v));
    const planTop = !planLook ? null : (
      <div data-patch="plan_41">
        <div className="flex items-center" style={{ gap: 10, paddingTop: 8 }}>
          <button type="button" onClick={() => setDetailAccount(null)} aria-label="Back" className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, background: '#FFFFFF', border: 0 }}>
            <ChevronLeft className="w-4 h-4" style={{ color: '#2B2C31' }} />
          </button>
          <div role="heading" aria-level="1" style={{ margin: 0, fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1, color: '#2B2C31' }}>{meta.label}{/* plan_42: RRSP and TFSA stay in capitals */}</div>
        </div>
        <div className="flex items-center flex-wrap" style={{ gap: 6, margin: '8px 4px 0', fontSize: 13, color: '#8B879A' }}>
          {prettyDate(win.start)} {'–'} {prettyDate(win.end)}
          {s.daysLeft > 0 && <span style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: '#ECE6FA', color: '#7A5DD3' }}>{s.daysLeft} days left</span>}
        </div>

        <div style={{ background: '#FFFFFF', borderRadius: 26, padding: '20px 18px', marginTop: 18 }} data-patch="planner_restyle">
          <div className="flex items-center" style={{ gap: 7, fontSize: 12, fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: plLook.ink }}>
            <span style={{ letterSpacing: 0 }}>{meta.emoji}</span>Put in so far
          </div>
          <div style={{ fontSize: 38, fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1, color: plLook.ink, marginTop: 10 }}>{formatCurrency(s.confirmed)}</div>
          <div style={{ fontSize: 14, color: PL_SOFT, marginTop: 8 }}>{hasTarget ? 'of your ' + formatCurrency(s.target) + ' target' : 'No target for ' + taxYear + ' yet'}</div>
          {hasTarget && (
            <>
              <div style={{ height: 150, margin: '14px -6px 0 -14px' }} data-testid="planner-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={plChart} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={'plannerFill_' + key} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={plLook.line} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={plLook.line} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEEAF6" vertical={false} />
                    <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.ceil(plChart.length / 5) - 1)} />
                    <YAxis width={46} tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={plTick} domain={[0, plTicks[plTicks.length - 1]]} ticks={plTicks} allowDecimals={false} />
                    <ReferenceLine y={s.target} stroke="#C4BADF" strokeDasharray="4 4" label={{ value: 'Target', position: 'insideTopRight', fontSize: 11, fill: '#9A8FBF' }} />
                    <Tooltip
                      cursor={{ stroke: plLook.line, strokeWidth: 1, strokeDasharray: '3 3' }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const row = payload.find((x) => x.value != null);
                        if (!row) return null;
                        return (
                          <div style={{ background: '#26252E', color: '#FFFFFF', borderRadius: 10, padding: '6px 10px', fontSize: 12, fontWeight: 600 }}>
                            {label} {'·'} {formatCurrency(row.value)}{row.dataKey === 'p' ? ' planned' : ''}
                          </div>
                        );
                      }}
                    />
                    <Area type="monotone" dataKey="p" stroke={onTrack ? '#8FD3AC' : '#F2AFC8'} strokeWidth={2.2} strokeDasharray="5 5" fill="none" connectNulls={false} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#FFFFFF', fill: onTrack ? '#8FD3AC' : '#F2AFC8' }} isAnimationActive={false} />
                    <Area type="monotone" dataKey="a" stroke={plLook.line} strokeWidth={2.2} fill={'url(#plannerFill_' + key + ')'} connectNulls={false} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#FFFFFF', fill: plLook.line }} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="relative overflow-hidden" style={{ height: 9, borderRadius: 9, background: '#F0EDF6', marginTop: 10 }}>
                <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: pct(s.projected) + '%', background: onTrack ? '#BFE3CC' : '#F2C3D5' }} />
                <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: pct(s.confirmed) + '%', background: plLook.line }} />
              </div>
              <div className="flex items-center flex-wrap" style={{ gap: 14, fontSize: 12, color: PL_SOFT, marginTop: 9 }}>
                <span className="whitespace-nowrap"><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 3, marginRight: 5, background: plLook.line }} />{formatCurrency(s.confirmed)} in</span>
                <span className="whitespace-nowrap"><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 3, marginRight: 5, background: onTrack ? '#BFE3CC' : '#F2C3D5' }} />{formatCurrency(s.pending + s.upcoming)} scheduled</span>
              </div>
            </>
          )}
          <div className="flex items-start" style={{ gap: 12, borderRadius: 18, padding: '13px 14px', marginTop: 16, background: !hasTarget ? '#ECE6FA' : onTrack ? '#DFF2E7' : '#FAE4ED' }}>
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 11, background: '#FFFFFF', fontSize: 15 }}>{!hasTarget ? '🎯' : onTrack ? '✅' : '🌸'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: !hasTarget ? '#2B2C31' : onTrack ? '#2F8756' : '#C04B7C' }}>
                {!hasTarget ? 'Set a target' : onTrack ? (surplus > 0 ? 'Past your target' : 'On track') : formatCurrency(shortBy) + ' short'}
              </div>
              <p className="m-0" style={{ fontSize: 13, color: '#55525F', lineHeight: 1.5, marginTop: 3 }}>
                {!hasTarget
                  ? "Worthly compares what you've put in and what's scheduled with your target, and tells you if you'll make it!"
                  : onTrack
                    ? "You're " + s.windowPct + '% through the window, and what\'s scheduled lands you at ' + formatCurrency(s.projected) + ' by ' + prettyShort(win.end) + (surplus > 0 ? ', ' + formatCurrency(surplus) + ' past your target!' : '.')
                    : "What's scheduled reaches " + formatCurrency(s.projected) + ' by ' + prettyShort(win.end) + '. Adding ' + formatCurrency(perMonth) + ' a month would close the gap.'}
              </p>
            </div>
          </div>
          {!hasTarget && (
            <button type="button" onClick={() => setTargetSheet({ account: key })} style={{ display: 'block', width: '100%', marginTop: 14, border: 0, borderRadius: 999, background: '#1F1E24', color: '#FFFFFF', padding: 15, fontSize: 15, fontWeight: 600 }}>
              Set your {taxYear} target
            </button>
          )}
        </div>

        <div className="grid grid-cols-3" style={{ gap: 8, marginTop: 10 }}>
          {[
            ['⏳', '#DFE9F9', 'Scheduled', formatCurrency(s.pending + s.upcoming)],
            ['🚪', '#DFF2E7', 'Room left', s.roomLeft == null ? 'Not set' : formatCurrency(s.roomLeft)],
            ['📅', '#FAE4ED', 'Deadline', prettyShort(win.end)],
          ].map(([e, bg, k, v]) => (
            <div key={k} style={{ background: '#FFFFFF', borderRadius: 18, padding: 12, minWidth: 0 }}>
              <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 11, background: bg, fontSize: 15 }}>{e}</span>
              <span style={{ display: 'block', fontSize: 11.5, color: PL_SOFT, marginTop: 8 }}>{k}</span>
              <span style={{ display: 'block', fontSize: String(v).length > 10 ? 12 : String(v).length > 8 ? 13 : 14.5, fontWeight: 600, color: '#2B2C31', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const card = planLook ? "bg-white rounded-[28px] overflow-hidden" : "bg-white rounded-[20px] shadow-sm overflow-hidden";
    const sansNum = planLook ? { fontWeight: 600 } : { fontFamily: "'DM Sans', sans-serif", fontWeight: 600 };
    const rowLine = { borderTop: planLook ? '1px solid #EEEAF6' : '1px solid #F5EBF0' };

    const actionRow = (label, onClick) => (
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2 text-[14.5px] font-semibold py-3.5"
        style={{ color: accent, fontFamily: "'DM Sans', sans-serif" }}
      >
        {label}
      </button>
    );

    // planner_restyle: the sections under the top card, in the new look.
    const plRowLine = { borderTop: '1px solid #EEEAF6' };
    const plAdd = (label, onClick, icon) => (
      <button type="button" onClick={onClick} className="w-full flex items-center text-left" style={{ gap: 12, padding: '12px 0', border: 0, background: 'none', fontSize: 14.5, fontWeight: 600, color: '#7A5DD3' }}>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: '#ECE6FA', color: '#7A5DD3', fontSize: icon ? 16 : 18 }}>{icon || '＋'}</span>
        {label}
      </button>
    );
    const plEmpty = (text) => <p className="m-0" style={{ fontSize: 13.5, lineHeight: 1.5, color: '#8B879A', padding: '12px 0' }}>{text}</p>;
    const plCard = { background: '#FFFFFF', borderRadius: 24, padding: '2px 16px' };
    const linkedIds = ((local.accounts[key] || {}).linkedAccountIds || []);
    const planSections = !planLook ? null : (
      <div data-patch="planner_restyle">
        {/* Sources */}
        {plEb('🗓️', 'Sources')}
        <div style={plCard}>
          {mySources.length === 0 && plEmpty('A contribution you make regularly, like a payroll deduction or a standing transfer. Add one and Worthly projects it forward for the year.')}
          {mySources.map((src, i) => {
            const per = s.perSource.find((p) => p.id === src.id) || {};
            const dot = src.status === 'stopped' ? '#C9C2C6' : SOURCE_COLORS[i % SOURCE_COLORS.length];
            return (
              <button key={src.id} type="button" onClick={() => setSourceSheet({ account: key, source: src })} className="w-full flex items-center text-left" style={{ gap: 12, padding: '12px 0', border: 0, background: 'none', ...(i ? plRowLine : {}) }}>
                {plSq(src.status === 'stopped' ? '⏸️' : '💼', tintOf(dot, 0.16))}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>{src.name}</span>
                  <span className="truncate" style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>
                    {FREQ_LABELS[src.frequency]}
                    {src.status === 'stopped' ? ' · stopped' : per.nextDate ? ' · next ' + prettyShort(per.nextDate) : ''}
                  </span>
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: '#2B2C31', flexShrink: 0 }}>{formatCurrency(src.amount)}</span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C4BADF' }} />
              </button>
            );
          })}
          <div style={plRowLine}>{plAdd('Add a source', () => setSourceSheet({ account: key, source: null }))}</div>
        </div>

        {/* Confirmed */}
        {plEb('✅', 'Confirmed', confirmedCount ? confirmedCount + (confirmedCount === 1 ? ' entry' : ' entries') : null)}
        <div className="overflow-hidden" style={{ ...plCard, padding: '0 16px' }}>
          {groups.length === 0 ? plEmpty('Nothing confirmed yet. Contributions show up here once their date comes and you confirm them, along with any one-offs you log.') : (
            groups.map((g, gi) => {
              const open = !!openConfirmed[g.key];
              return (
                <div key={g.key} style={gi === 0 ? undefined : plRowLine}>
                  <button type="button" onClick={() => setOpenConfirmed((o) => ({ ...o, [g.key]: !o[g.key] }))} className="w-full flex items-center text-left" style={{ gap: 12, padding: '12px 0', border: 0, background: 'none' }}>
                    {plSq(g.key === '__loose' ? '✍️' : '💼', tintOf(g.color, 0.16))}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>{g.name}</span>
                      <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>{g.meta}</span>
                    </span>
                    <span style={{ fontSize: 14.5, fontWeight: 600, color: '#2B2C31', flexShrink: 0 }}>{formatCurrency(g.total)}</span>
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: '#C4BADF', transform: open ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  {open && (
                    <div style={{ background: '#F7F5FB', margin: '0 -16px', padding: '0 16px' }}>
                      {g.rows.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            if (r.occ) setConfirmSheet(r.occ);
                            else if (r.contrib) setOneOffSheet({ account: key, existing: r.contrib });
                            else if (r.srcId) {
                              const src = mySources.find((x) => x.id === r.srcId);
                              if (src) setSourceSheet({ account: key, source: src });
                            } else if (r.openingRow) setTargetSheet({ account: key });
                          }}
                          className="w-full flex items-center text-left"
                          style={{ gap: 12, padding: '10px 0 10px 52px', border: 0, background: 'none', borderTop: '1px solid #EEEAF6' }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="truncate" style={{ display: 'block', fontSize: 13.5, color: '#55525F' }}>{r.label}</span>
                            <span style={{ display: 'block', fontSize: 12, color: '#A9A4B8', marginTop: 1 }}>
                              {prettyShort(r.date)}{r.skipped ? ' · skipped' : ''}{r.oneoff ? ' · one-off' : ''}
                            </span>
                          </span>
                          <span style={{ fontSize: 13.5, fontWeight: r.skipped ? 500 : 600, color: r.skipped ? '#A9A4B8' : '#55525F', flexShrink: 0 }}>{r.skipped ? '—' : formatCurrency(r.amount)}</span>
                          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#D6D0E6' }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Linked accounts */}
        {plEb('🔗', 'Linked accounts', linkedIds.length ? (
          <button type="button" onClick={() => setLinkSheet({ account: key })} style={{ border: 0, background: 'none', padding: 0, fontSize: 13, fontWeight: 500, color: '#7A5DD3' }}>Edit</button>
        ) : null)}
        <div style={plCard}>
          {linkedIds.length === 0 ? (
            <>
              {plEmpty('Link the account you transfer into in your Budget, and those transfers show up on the Overview for you to confirm.')}
              <div style={plRowLine}>{plAdd('Link an account', () => setLinkSheet({ account: key }))}</div>
            </>
          ) : linkedIds.map((id, i) => {
            const inv = (investmentAccounts || []).find((x) => String(x.id) === String(id));
            return (
              <div key={id} className="flex items-center" style={{ gap: 12, padding: '12px 0', ...(i ? plRowLine : {}) }}>
                {plSq((inv && inv.emoji) || '📈', '#ECE6FA')}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>{acctName(id)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>Transfers show up on the Overview to confirm</span>
                </span>
              </div>
            );
          })}
        </div>

        {/* Contribution room */}
        {plEb('🚪', 'Contribution room')}
        <div style={plCard}>
          {s.roomAvailable == null ? (
            plEmpty(key === 'rrsp'
              ? 'Room left stays blank until you enter it. Your deduction limit is on your notice of assessment for last year, or in your CRA account.'
              : 'Room left stays blank until you enter it. Your total available room is in your CRA account.')
          ) : (
            <>
              <div className="flex items-center" style={{ gap: 12, padding: '12px 0' }}>
                {plSq('🧾', '#DFF2E7')}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>Your {taxYear} room</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>{key === 'rrsp' ? 'Your deduction limit' : 'Your available room'}</span>
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: '#2B2C31', flexShrink: 0 }}>{formatCurrency(s.roomAvailable)}</span>
              </div>
              <div className="flex items-center" style={{ gap: 12, padding: '12px 0', ...plRowLine }}>
                {plSq('📉', '#FBF3D6')}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31' }}>After your plan</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>{s.roomAfterPlan < 0 ? 'Over your room!' : 'If everything scheduled goes in'}</span>
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: s.roomAfterPlan < 0 ? '#C04B7C' : '#2B2C31', flexShrink: 0 }}>{formatCurrency(s.roomAfterPlan)}</span>
              </div>
              <div style={{ padding: '4px 0 12px' }}>
                <div className="relative overflow-hidden" style={{ height: 8, borderRadius: 9, background: '#F0EDF6' }}>
                  <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: Math.min(100, (s.projected / Math.max(1, s.roomAvailable)) * 100) + '%', background: '#C9B8F2' }} />
                  <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: Math.min(100, (s.confirmed / Math.max(1, s.roomAvailable)) * 100) + '%', background: plLook.line }} />
                </div>
                <p className="m-0" style={{ fontSize: 12, color: '#8B879A', marginTop: 8, lineHeight: 1.5 }}>
                  <span style={{ color: plLook.ink, fontWeight: 600 }}>{formatCurrency(s.confirmed)}</span> used {'·'}{' '}
                  <span style={{ color: '#7A5DD3', fontWeight: 600 }}>{formatCurrency(s.pending + s.upcoming)}</span> more planned {'·'}{' '}
                  <span style={{ color: s.roomAfterPlan < 0 ? '#C04B7C' : '#8B879A', fontWeight: 600 }}>{formatCurrency(Math.abs(s.roomAfterPlan))}</span>
                  {s.roomAfterPlan < 0 ? ' over your room' : ' still free'}
                </p>
              </div>
            </>
          )}
          <div style={plRowLine}>
            {s.roomAvailable == null
              ? plAdd('Add your ' + taxYear + ' room', () => setTargetSheet({ account: key }))
              : plAdd('Edit goal & room', () => setTargetSheet({ account: key }), '✏️')}
          </div>
        </div>

        <button type="button" onClick={() => setOneOffSheet({ account: key })} style={{ display: 'block', width: '100%', border: 0, borderRadius: 999, background: '#1F1E24', color: '#FFFFFF', padding: 16, fontSize: 15.5, fontWeight: 600, margin: '26px 0 32px' }}>
          Add a one-off contribution
        </button>
      </div>
    );

    return (
      <div>
        {planLook ? planTop : (<>
        {/* Header */}
        <div className="flex items-center gap-3 px-1 pt-3 pb-1">
          <button onClick={() => setDetailAccount(null)} className="flex items-center justify-center">
            <ChevronLeft className="w-5 h-5" style={{ color: accent }} />
          </button>
          <h1
            className="m-0"
            style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', fontWeight: 400, color: '#1A1A1F' }}
          >
            {meta.label}
          </h1>
        </div>
        <p className="m-0 px-1 pb-5 text-[11.5px]" style={{ color: '#8C8789', marginLeft: '32px' }}>
          {prettyDate(win.start)} {'\u2013'} {prettyDate(win.end)}
          {s.daysLeft > 0 && (
            <>
              {' \u00b7 '}
              <b style={{ color: accent }}>{s.daysLeft} days left</b>
            </>
          )}
        </p>

        {/* Hero - the one number that carries the account's own colour */}
        <div className="rounded-[22px] shadow-sm mb-8" style={{ background: tile.bg, padding: '24px 22px' }}>
          <p
            className="m-0"
            style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '44px', lineHeight: 1, color: tile.value }}
          >
            {formatCurrency(s.confirmed)}
          </p>
          <p className="m-0 mt-3 text-[13.5px]" style={{ color: tile.value, opacity: 0.85 }}>
            {hasTarget
              ? 'of your ' + formatCurrency(s.target) + ' target'
              : 'No target set for ' + taxYear}
          </p>

          {hasTarget && (
            <>
              <div
                className="relative rounded-full overflow-hidden"
                style={{ height: '9px', background: '#FFFFFF', marginTop: '22px' }}
              >
                <div
                  className="absolute top-0 bottom-0 rounded-full"
                  style={{ left: 0, width: pct(s.projected) + '%', background: onTrack ? planGreen : planAmber }}
                />
                <div
                  className="absolute top-0 bottom-0 rounded-full"
                  style={{ left: 0, width: pct(s.confirmed) + '%', background: tile.value }}
                />
              </div>

              <div className="flex items-center mt-3 text-[12px]" style={{ gap: '7px', color: tile.value }}>
                <span className="flex-shrink-0" style={{ width: '9px', height: '9px', borderRadius: '3px', background: tile.value }} />
                <span className="whitespace-nowrap">{formatCurrency(s.confirmed)} in</span>
                <span
                  className="flex-shrink-0"
                  style={{ width: '9px', height: '9px', borderRadius: '3px', marginLeft: '5px', background: onTrack ? planGreen : planAmber }}
                />
                <span className="whitespace-nowrap">{formatCurrency(s.pending + s.upcoming)} scheduled</span>
                <span
                  className="ml-auto flex-shrink-0 text-[11.5px] font-bold rounded-full whitespace-nowrap"
                  style={{
                    padding: '5px 10px',
                    background: onTrack ? '#EEF5E9' : '#FDF0E2',
                    color: onTrack ? '#2F6B44' : '#8A5A1E',
                  }}
                >
                  {onTrack
                    ? (surplus > 0 ? 'Past target' : 'On target')
                    : formatCurrency(-surplus) + ' short'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Status */}
        <div className="mb-8">
          <Eyebrow><span className="wy-star">✦ </span>Status</Eyebrow>
          <div className="rounded-[20px] shadow-sm" style={{ background: tone.bg, padding: '18px 20px' }}>
            <div className="flex items-center gap-2.5">
              {tone.dot && (
                <span className="rounded-full flex-shrink-0" style={{ width: '8px', height: '8px', background: tone.dot }} />
              )}
              <span className="text-[15.5px] font-bold" style={{ color: tone.fg }}>
                {!hasTarget
                  ? 'Set a target to see where you stand'
                  : onTrack
                    ? 'On track'
                    : formatCurrency(shortBy) + ' short'}
              </span>
            </div>
            <p className="m-0 mt-2.5 text-[13.5px] leading-relaxed" style={{ color: tone.body }}>
              {!hasTarget
                ? 'Worthly compares what you have put in and what is scheduled against your target, and tells you if you will make it.'
                : onTrack
                  ? 'You are ' + s.windowPct + '% through the window, and your scheduled contributions land you at ' +
                    formatCurrency(s.projected) + ' by ' + prettyShort(win.end) + ' \u2014 ' +
                    formatCurrency(surplus) + ' past target.'
                  : 'Your scheduled contributions reach ' + formatCurrency(s.projected) + ' by ' +
                    prettyShort(win.end) + '. Adding ' + formatCurrency(perMonth) +
                    ' a month would close the gap.'}
            </p>
            {!hasTarget && (
              <div style={rowLine} className="mt-3.5">
                {actionRow('Set your ' + taxYear + ' target', () => setTargetSheet({ account: key }))}
              </div>
            )}
          </div>
        </div>

        </>)}
        {planLook ? planSections : (<>{/* planner_restyle: the old look stays for the other themes */}
        {/* Linked accounts */}
        <div className="mb-8">
          <Eyebrow><span className="wy-star">✦ </span>Linked accounts</Eyebrow>
          <div className={card} style={{ padding: '14px 18px' }}>
            {(() => {
              const ids = ((local.accounts[key] || {}).linkedAccountIds || []);
              return (
                <>
                  <p className="m-0 text-[13px] leading-relaxed" style={{ color: '#8C8789' }}>
                    {ids.length
                      ? 'Transfers into these accounts in your Budget show up on the Overview for you to confirm.'
                      : 'Link the account you transfer into in your Budget, and those transfers will show up here as suggested contributions.'}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {ids.map((id) => (
                      <span key={id} className="text-[12.5px] font-semibold rounded-full px-2.5 py-1" style={{ background: track, color: eyebrow }}>
                        {acctName(id)} {'✓'}
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => setLinkSheet({ account: key })}
                      className="text-[12.5px] font-semibold rounded-full px-2.5 py-1"
                      style={{ border: '1.5px dashed #D9CCE3', color: '#8C8789' }}
                    >
                      {ids.length ? 'Edit' : '＋ Link an account'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Sources */}
        <div className="mb-8">
          <Eyebrow><span className="wy-star">✦ </span>Sources</Eyebrow>
          <div className={card} style={{ padding: '6px 18px' }}>
            {mySources.length === 0 ? (
              <p className="m-0 py-3 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
                A source is a contribution you make regularly {'\u2014'} a payroll deduction, a standing
                transfer. Add one and Worthly projects it forward for the year.
              </p>
            ) : (
              <>
                {mySources.map((src, i) => {
                  const per = s.perSource.find((p) => p.id === src.id) || {};
                  const dotColor = src.status === 'stopped' ? '#C9C2C6' : SOURCE_COLORS[i % SOURCE_COLORS.length];
                  return (
                    <button
                      key={src.id}
                      onClick={() => setSourceSheet({ account: key, source: src })}
                      className="w-full flex items-center gap-3 py-3.5 text-left"
                      style={i === 0 ? undefined : rowLine}
                    >
                      <span className="rounded-full flex-shrink-0" style={{ width: '9px', height: '9px', background: dotColor }} />
                      <span className="min-w-0">
                        <span className="block text-[15.5px]" style={labelStyle}>{src.name}</span>
                        <span className="block text-[13px] mt-1" style={{ color: '#8C8789' }}>
                          {formatCurrency(src.amount)} {FREQ_LABELS[src.frequency].toLowerCase()}
                          {src.status === 'stopped'
                            ? ' \u00b7 stopped'
                            : per.nextDate
                              ? ' \u00b7 next ' + prettyShort(per.nextDate)
                              : ''}
                        </span>
                      </span>
                      <span
                        className="ml-auto rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ width: '28px', height: '28px', background: track }}
                      >
                        <Pencil className="w-3.5 h-3.5" style={{ color: accent }} />
                      </span>
                    </button>
                  );
                })}
              </>
            )}
            <div style={mySources.length === 0 ? rowLine : rowLine}>
              {actionRow('+ Add a source', () => setSourceSheet({ account: key, source: null }))}
            </div>
          </div>
        </div>

        {/* Confirmed */}
        <div className="mb-8">
          <Eyebrow><span className="wy-star">✦ </span>Confirmed{confirmedCount ? ' \u00b7 ' + confirmedCount : ''}</Eyebrow>
          {/* No vertical padding: an open group's tint bleeds to the card edges,
              and padding here leaves a white band under the last row. The rows
              carry their own. */}
          <div className={card} style={{ padding: '0 18px' }}>
            {groups.length === 0 ? (
              <p className="m-0 py-3 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
                Nothing confirmed yet. Contributions appear here once their date arrives and you
                confirm them, along with any one-offs you log.
              </p>
            ) : (
              groups.map((g, gi) => {
                const open = !!openConfirmed[g.key];
                return (
                  <div key={g.key} style={gi === 0 ? undefined : rowLine}>
                    <button
                      onClick={() => setOpenConfirmed((o) => ({ ...o, [g.key]: !o[g.key] }))}
                      className="w-full flex items-center gap-3 py-3.5 text-left"
                    >
                      <span
                        className="rounded-full flex-shrink-0"
                        style={{ width: '9px', height: '9px', background: g.color }}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className="block text-[15.5px] truncate"
                          style={{ ...labelStyle, color: g.nameColor, fontWeight: 700 }}
                        >
                          {g.name}
                        </span>
                        <span className="block text-[13px] mt-1" style={{ color: '#8C8789' }}>{g.meta}</span>
                      </span>
                      <span className="text-[15.5px] flex-shrink-0" style={{ ...sansNum, color: '#1F2937' }}>
                        {formatCurrency(g.total)}
                      </span>
                      <ChevronDown
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: '#a39fa0', transform: open ? 'rotate(180deg)' : 'none' }}
                      />
                    </button>

                    {open && (
                      <div style={{ background: tintOf(g.nameColor, 0.1), margin: '0 -18px', padding: '0 18px' }}>
                        {g.rows.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => {
                              // One rule for every row: tapping opens wherever
                              // that thing is actually edited.
                              if (r.occ) setConfirmSheet(r.occ);
                              else if (r.contrib) setOneOffSheet({ account: key, existing: r.contrib });
                              else if (r.srcId) {
                                const src = mySources.find((x) => x.id === r.srcId);
                                if (src) setSourceSheet({ account: key, source: src });
                              } else if (r.openingRow) setTargetSheet({ account: key });
                            }}
                            className="w-full flex items-center gap-3 py-2.5 text-left"
                            style={{ paddingLeft: '20px', borderTop: '1px solid rgba(0,0,0,0.035)' }}
                          >
                            <span className="min-w-0 flex-1">
                              <span
                                className="block text-[13.5px] truncate"
                                style={{ color: '#5C5459', fontWeight: 400 }}
                              >
                                {r.label}
                              </span>
                              <span className="block text-[12px] mt-0.5" style={{ color: '#B0A7AD' }}>
                                {prettyShort(r.date)}
                                {r.skipped ? ' \u00b7 skipped' : ''}
                                {r.oneoff ? ' \u00b7 one-off' : ''}
                              </span>
                            </span>
                            <span
                              className="text-[13.5px] flex-shrink-0"
                              style={{ ...sansNum, color: r.skipped ? '#B0A7AD' : '#5C5459', fontWeight: r.skipped ? 500 : 600 }}
                            >
                              {r.skipped ? '\u2014' : formatCurrency(r.amount)}
                            </span>
                            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#D6CCD2' }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Contribution room */}
        <div className="mb-8">
          <Eyebrow><span className="wy-star">✦ </span>Contribution room</Eyebrow>
          <div className={card} style={{ padding: '16px 16px 2px' }}>
            {s.roomAvailable == null ? (
              <p className="m-0 pb-1 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
                Room left stays blank until you enter it.{' '}
                {key === 'rrsp'
                  ? 'Your deduction limit can be found on your notice of assessment for last year, or in your CRA account.'
                  : 'Your total available room can be found in your CRA account.'}
              </p>
            ) : (
              <>
                <div className="flex gap-2.5 mb-3">
                  <div className="flex-1 rounded-2xl" style={{ background: tile.bg, padding: '14px 13px' }}>
                    <span className="block text-[11.5px] font-semibold" style={{ color: tile.label, opacity: 0.85 }}>
                      Your {taxYear} room
                    </span>
                    <span className="block text-[16px] mt-1.5" style={{ ...sansNum, color: tile.value }}>
                      {formatCurrency(s.roomAvailable)}
                    </span>
                  </div>
                  <div className="flex-1 rounded-2xl" style={{ background: tilePalette[2].bg, padding: '14px 13px' }}>
                    <span className="block text-[11.5px] font-semibold" style={{ color: tilePalette[2].label, opacity: 0.85 }}>
                      After your plan
                    </span>
                    <span
                      className="block text-[16px] mt-1.5"
                      style={{ ...sansNum, color: s.roomAfterPlan < 0 ? '#B91C1C' : tilePalette[2].value }}
                    >
                      {formatCurrency(s.roomAfterPlan)}
                    </span>
                  </div>
                </div>
                <div className="relative rounded-full" style={{ height: '7px', background: track, margin: '4px 2px 10px' }}>
                  <div
                    className="absolute top-0 bottom-0 rounded-full"
                    style={{ left: 0, width: Math.min(100, (s.projected / s.roomAvailable) * 100) + '%', background: alt }}
                  />
                  <div
                    className="absolute top-0 bottom-0 rounded-full"
                    style={{ left: 0, width: Math.min(100, (s.confirmed / s.roomAvailable) * 100) + '%', background: tile.value }}
                  />
                </div>
                <p className="m-0 px-0.5 pb-1 text-[12px]" style={{ color: '#8C8789' }}>
                  {/* Each figure is tinted to the bar segment it describes, so the
                      key needs no swatches of its own. */}
                  <span style={{ color: tile.value, fontWeight: 600 }}>{formatCurrency(s.confirmed)}</span> used
                  {' \u00b7 '}
                  <span style={{ color: alt, fontWeight: 600 }}>{formatCurrency(s.pending + s.upcoming)}</span> more planned
                  {s.roomAfterPlan != null && (
                    <>
                      {' \u00b7 '}
                      <span style={{ color: s.roomAfterPlan < 0 ? '#B91C1C' : '#8C8789', fontWeight: 600 }}>
                        {formatCurrency(Math.abs(s.roomAfterPlan))}
                      </span>
                      {s.roomAfterPlan < 0 ? ' over your room' : ' still free'}
                    </>
                  )}
                </p>
              </>
            )}
            <div style={rowLine}>
              {actionRow(s.roomAvailable == null ? 'Add your ' + taxYear + ' room' : 'Edit goal & room',
                () => setTargetSheet({ account: key }))}
            </div>
          </div>
        </div>

        <button
          onClick={() => setOneOffSheet({ account: key })}
          className="w-full rounded-full text-white text-[15px] font-semibold mb-8"
          style={{ background: accent, padding: '16px', fontFamily: "'DM Sans', sans-serif" }}
        >
          Add a one-off contribution
        </button>
        </>)}
      </div>
    );
  };

  /* ---------------- plan_41: Money plan look ---------------- */
  const PL = { ink: '#2B2C31', soft: '#8B879A', line: '#EEEAF6', field: '#F7F5FB' };
  const plInfoBtn = (
    <button type="button" aria-label="How it works" onClick={() => setShowInfo(true)} style={{ background: '#FFFFFF', border: 0, cursor: 'pointer', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button>
  );
  const plChip = (text, bg, fg) => (
    <span style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: bg, color: fg, whiteSpace: 'nowrap' }}>{text}</span>
  );
  const renderPlanHeader = () => (
    <div data-patch="plan_41">
      <div className="flex items-center" style={{ gap: 10, paddingTop: 8 }}>
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Back" className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, background: '#FFFFFF', border: 0 }}>
            <ChevronLeft className="w-4 h-4" style={{ color: PL.ink }} />
          </button>
        )}
        <div role="heading" aria-level="1" style={{ margin: 0, fontSize: 25, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.15, color: PL.ink, minWidth: 0 }}>
          contributions planner<span style={{ display: 'inline-block', marginLeft: 8, verticalAlign: 3 }}>{plInfoBtn}</span>
        </div>
      </div>
      <div className="flex items-center" style={{ gap: 10, marginTop: 12, padding: '0 4px' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: PL.soft }}>RRSP and TFSA, against your targets</span>
        <span className="flex items-center flex-shrink-0" style={{ gap: 4, background: '#FFFFFF', borderRadius: 999, padding: 5 }}>
          <button type="button" onClick={() => setTaxYear((y) => y - 1)} aria-label="Previous year" className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: 999, background: PL.field, border: 0 }}>
            <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#6C6879' }} />
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, padding: '0 4px', color: PL.ink }}>{taxYear}</span>
          <button type="button" onClick={() => setTaxYear((y) => y + 1)} aria-label="Next year" className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: 999, background: PL.field, border: 0 }}>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: '#6C6879' }} />
          </button>
        </span>
      </div>
      <div className="flex justify-center" style={{ gap: 44, margin: '20px 0 18px' }}>
        {[{ k: 'overview', label: 'Overview' }, { k: 'schedule', label: 'Schedule' }].map((t) => (
          <button key={t.k} type="button" onClick={() => setView(t.k)} className="relative" style={{ border: 0, background: 'none', padding: '0 0 8px', fontSize: 15, fontWeight: view === t.k ? 600 : 500, color: view === t.k ? PL.ink : '#9E9AAB' }}>
            {t.label}
            {view === t.k && <span className="absolute left-1/2 bottom-0" style={{ width: 34, height: 3, borderRadius: 3, background: '#A58BE6', transform: 'translateX(-50%)' }} />}
          </button>
        ))}
      </div>
    </div>
  );

  // planner_restyle: one look for the planner - white cards, an emoji square on
  // every number and row, and emoji eyebrows for the section names.
  const plEb = (emoji, title, right) => (
    <div className="flex items-center" style={{ gap: 7, margin: '26px 4px 10px', fontSize: 12, fontWeight: 600, letterSpacing: '1.4px', textTransform: 'uppercase', color: '#7A5DD3' }}>
      <span style={{ letterSpacing: 0 }}>{emoji}</span>{title}
      {right != null && <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontSize: 13, fontWeight: 500, color: PL.soft }}>{right}</span>}
    </div>
  );
  const plSq = (emoji, bg, small) => (
    <span className="flex items-center justify-center flex-shrink-0" style={{ width: small ? 32 : 40, height: small ? 32 : 40, borderRadius: small ? 11 : 13, background: bg, fontSize: small ? 15 : 19 }}>{emoji}</span>
  );
  const PL_ACC = { rrsp: { wash: '#DFE9F9', ink: '#3C6DB5', bar: '#5C86C9' }, tfsa: { wash: '#DFF2E7', ink: '#2F8756', bar: '#4FA877' } };
  const plBar = (s, L, onTrack) => {
    const scale = Math.max(s.target, 1);
    const p = (n) => Math.max(0, Math.min(100, (n / scale) * 100));
    return (
      <div className="relative overflow-hidden" style={{ height: 9, borderRadius: 9, background: '#F0EDF6' }}>
        <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: p(s.projected) + '%', background: onTrack ? '#BFE3CC' : '#F2C3D5' }} />
        <div className="absolute top-0 bottom-0" style={{ left: 0, borderRadius: 9, width: p(s.confirmed) + '%', background: L.bar }} />
      </div>
    );
  };

  const renderPlanOverview = () => {
    const PILL = {
      match: ['Transfer found', '#DFF2E7', '#2F8756'],
      due: ['Waiting', '#FAE4ED', '#C04B7C'],
      transfer: ['Unscheduled', '#ECE6FA', '#7A5DD3'],
    };
    return (
      <div data-patch="planner_restyle">
        <div className="grid grid-cols-2" style={{ gap: 10 }}>
          {[
            ['🏦', 'Put in this year', formatCurrency(combined.confirmed), '#ECE6FA', '#7A5DD3'],
            ['🚪', 'Room left', combined.roomComplete ? formatCurrency(combined.roomLeft) : 'Not set', '#DFF2E7', combined.roomComplete ? '#2F8756' : PL.soft],
          ].map(([e, k, v, bg, ink]) => (
            <div key={k} style={{ background: '#FFFFFF', borderRadius: 22, padding: 14, minWidth: 0 }}>
              {plSq(e, bg, true)}
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: PL.soft, marginTop: 10 }}>{k}</span>
              <span className="truncate" style={{ display: 'block', fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 2, color: ink }}>{v}</span>
            </div>
          ))}
        </div>

        {reviewItems.length > 0 && (
          <div data-testid="planner-review">
            {plEb('💌', 'To review', reviewItems.length + (reviewItems.length === 1 ? ' item' : ' items'))}
            <div style={{ background: '#FFFFFF', borderRadius: 24, padding: '2px 16px' }}>
              {reviewItems.map((it, idx) => {
                const isTransfer = it.kind === 'transfer';
                const p = PILL[it.kind];
                return (
                  <button
                    key={it.kind + ':' + (it.occ ? it.occ.occurrenceId : '') + ':' + (it.transfer ? it.transfer.id : '')}
                    type="button"
                    onClick={() => setReviewOpen(it)}
                    className="w-full flex items-center text-left"
                    style={{ gap: 12, padding: '12px 0', border: 0, background: 'none', borderTop: idx === 0 ? 'none' : '1px solid ' + PL.line }}
                  >
                    {plSq(isTransfer ? '💸' : (it.account === 'rrsp' ? ACCOUNTS[0].emoji : ACCOUNTS[1].emoji), isTransfer ? '#ECE6FA' : PL_ACC[it.account === 'rrsp' ? 'rrsp' : 'tfsa'].wash)}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: PL.ink }}>{isTransfer ? 'Transfer to ' + acctName(it.transfer.accountId) : it.occ.sourceName}</span>
                      <span style={{ display: 'block', fontSize: 12.5, color: PL.soft, marginTop: 2 }}>{prettyShort(it.date)} {'·'} {isTransfer ? 'from Budget' : (it.account === 'rrsp' ? 'RRSP' : 'TFSA')}</span>
                    </span>
                    <span className="flex flex-col items-end flex-shrink-0" style={{ gap: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: PL.ink }}>{formatCurrency(isTransfer ? it.transfer.amount : it.occ.plannedAmount)}</span>
                      {plChip(p[0], p[1], p[2])}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {plEb('💼', 'Your accounts')}
        {ACCOUNTS.map((a, i) => {
          const s = summaries[a.key];
          const L = PL_ACC[a.key];
          const hasTarget = s.target > 0;
          const surplus = s.projected - s.target;
          const onTrack = hasTarget && surplus >= 0;
          const next = (s.occurrences || []).find((o) => o.state === 'upcoming');
          const pill = !hasTarget
            ? ['Set up', L.wash, L.ink]
            : onTrack
              ? [surplus > 0 ? 'Past target ✓' : 'On track ✓', '#DFF2E7', '#2F8756']
              : [formatCurrency(-surplus) + ' short', '#FAE4ED', '#C04B7C'];
          return (
            <button key={a.key} type="button" onClick={() => setDetailAccount(a.key)} className="w-full text-left" style={{ display: 'block', background: '#FFFFFF', borderRadius: 24, padding: 16, border: 0, marginTop: i ? 10 : 0 }} data-testid={'planner-account-' + a.key}>
              <span className="flex items-center" style={{ gap: 12 }}>
                {plSq(a.emoji, L.wash)}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: PL.ink }}>{a.label}</span>
                  <span className="truncate" style={{ display: 'block', fontSize: 12.5, color: PL.soft, marginTop: 1 }}>
                    {'Until ' + prettyDate(s.window.end) + (s.daysLeft > 0 ? ' · ' + s.daysLeft + ' days left' : '')}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C4BADF' }} />
              </span>
              {(hasTarget || s.confirmed > 0) && (
                <span className="flex items-baseline" style={{ gap: 6, margin: '14px 0 9px' }}>
                  <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.7px', color: L.ink }}>{formatCurrency(s.confirmed)}</span>
                  <span className="truncate" style={{ fontSize: 13, color: PL.soft }}>{hasTarget ? 'of ' + formatCurrency(s.target) + ' · ' + s.targetPct + '%' : 'put in so far'}</span>
                </span>
              )}
              {hasTarget && plBar(s, L, onTrack)}
              <span className="flex items-center justify-between" style={{ gap: 10, marginTop: 12, fontSize: 12.5, color: PL.soft }}>
                <span className="truncate">
                  {next
                    ? 'Next: ' + formatCurrency(next.plannedAmount) + ' on ' + prettyShort(next.date)
                    : hasTarget ? 'Nothing scheduled yet' : "Set a target to see if you'll make it"}
                </span>
                {plChip(pill[0], pill[1], pill[2])}
              </span>
            </button>
          );
        })}

        {!isSetUp && (
          <>
            {plEb('✨', 'How it works')}
            <div style={{ background: '#FFFFFF', borderRadius: 24, padding: '2px 16px' }}>
              {[
                ['🎯', '#ECE6FA', 'Pick a target', 'How much you want in each account this year'],
                ['💼', '#DFE9F9', 'Add what you already put in', 'A payroll deduction or a standing transfer'],
                ['✅', '#DFF2E7', "See if you'll get there", 'Worthly works it out and tells you'],
              ].map(([e, bg, t, b], idx) => (
                <div key={t} className="flex items-center" style={{ gap: 12, padding: '12px 0', borderTop: idx ? '1px solid ' + PL.line : 'none' }}>
                  {plSq(e, bg)}
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: PL.ink }}>{t}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: PL.soft, marginTop: 2 }}>{b}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <p style={{ fontSize: 11.5, color: '#A9A4B8', lineHeight: 1.55, margin: '22px 6px 4px', textAlign: 'center' }}>
          Worthly tracks what you enter here. Your contribution room, deadlines and what you can actually deduct may differ from the CRA's own records. This is not tax advice, financial advice or a promise of a refund. Please do your own calculations too, and don't rely on the numbers here when filing your taxes.
        </p>
      </div>
    );
  };

  return (
    <div className="px-4 pt-3">
      {!detailAccount && planLook && renderPlanHeader()}
      {!detailAccount && !planLook && (
      <>
      {/* Purple: header and tabs sit in the gradient band (wy-ph). */}
      <div className="wy-ph" style={{ '--ph-lift': '0.75rem', '--ph-pad': '0rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-1 pt-3">
        <h1 className="m-0 flex items-center" style={{ fontFamily: "'DM Serif Display', serif", fontSize: onBack ? '24px' : '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Back to Home"
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: track, marginRight: '10px' }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: accent }} />
            </button>
          )}
          Contributions Planner
          <button
            onClick={() => setShowInfo(true)}
            style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, marginLeft: '8px', fontFamily: "'DM Sans', sans-serif" }}
          >i</button>
        </h1>
        <div className="flex items-center gap-2" style={{ color: muted }}>
          <button onClick={() => setTaxYear((y) => y - 1)} className="w-4 h-4 flex items-center justify-center">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-[15px] font-medium">{taxYear}</span>
          <button onClick={() => setTaxYear((y) => y + 1)} className="w-4 h-4 flex items-center justify-center">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mt-4 mb-5 wy-tabs" style={{ gap: '48px' }}>
        {[
          { k: 'overview', label: 'Overview' },
          { k: 'schedule', label: 'Schedule' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setView(t.k)}
            data-on={view === t.k ? '1' : undefined}
            className="relative pb-2 text-[15px]"
            style={{
              color: view === t.k ? '#1F2937' : muted,
              fontWeight: view === t.k ? 700 : 500,
            }}
          >
            {t.label}
            {view === t.k && (
              <span
                className="absolute left-1/2 bottom-0 h-[3px] rounded-full"
                style={{ width: '34px', transform: 'translateX(-50%)', background: accent }}
              />
            )}
          </button>
        ))}
      </div>
      </div>
      </>
      )}

      {detailAccount && renderAccountDetail()}

      {!detailAccount && view === 'overview' && planLook && renderPlanOverview()}
      {!detailAccount && view === 'overview' && !planLook && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <Tile
              emoji={'\ud83c\udfe6'}
              label="Total contributed"
              value={formatCurrency(combined.confirmed)}
              tone={2}
            />
            <Tile
              emoji={'\ud83d\udeaa'}
              label="Room left"
              value={combined.roomComplete ? formatCurrency(combined.roomLeft) : 'Not set'}
              tone={3}
            />
          </div>

          <Eyebrow><span className="wy-star">✦ </span>Your accounts</Eyebrow>
          <div className="grid grid-cols-2 gap-3">
            {ACCOUNTS.map((a, i) => {
              const s = summaries[a.key];
              // Slot, not hex - so the pairing remaps with the theme.
              const tile = tilePalette[i === 0 ? 0 : 1];
              const hasTarget = s.target > 0;
              const surplus = s.projected - s.target;
              const onTrack = hasTarget && surplus >= 0;
              // Fixed across themes on purpose: a status is a state, not decoration.
              const pill = !hasTarget
                ? { fg: tile.value, dot: null, text: 'Set up' }
                : onTrack
                  ? { fg: '#2F6B44', dot: '#2F6B44', text: 'On track' }
                  : { fg: '#8A5A1E', dot: '#C98A2E', text: formatCurrency(-surplus) + ' short' };
              const fill = hasTarget ? Math.max(0, Math.min(100, (s.confirmed / s.target) * 100)) : 0;

              return (
                <button
                  key={a.key}
                  onClick={() => setDetailAccount(a.key)}
                  className="rounded-[20px] text-left"
                  style={{ background: tile.bg, padding: '20px 18px' }}
                >
                  <span
                    className="rounded-lg flex items-center justify-center"
                    style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '14px', marginBottom: '14px' }}
                  >
                    {a.emoji}
                  </span>
                  <span
                    className="block"
                    style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: tile.label, opacity: 0.85 }}
                  >
                    {a.label}
                  </span>
                  <span
                    className="block"
                    style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '29px', lineHeight: 1, color: tile.value, margin: '11px 0 5px' }}
                  >
                    {formatCurrency(s.confirmed)}
                  </span>
                  <span className="block text-[12px]" style={{ color: tile.value, opacity: 0.8 }}>
                    {hasTarget ? 'of ' + formatCurrency(s.target) : 'No target set'}
                  </span>
                  <span
                    className="block relative rounded-full overflow-hidden"
                    style={{ height: '6px', background: 'rgba(255,255,255,0.6)', margin: '17px 0 13px' }}
                  >
                    <span
                      className="absolute top-0 bottom-0 rounded-full"
                      style={{ left: 0, width: fill + '%', background: tile.value }}
                    />
                  </span>
                  <span
                    className="inline-flex items-center rounded-full"
                    style={{ gap: '6px', background: 'rgba(255,255,255,0.72)', padding: '6px 11px', fontSize: '11.5px', fontWeight: 700, color: pill.fg }}
                  >
                    {pill.dot && (
                      <span className="rounded-full" style={{ width: '7px', height: '7px', background: pill.dot, display: 'inline-block' }} />
                    )}
                    {pill.text}
                  </span>
                </button>
              );
            })}
          </div>

          {reviewItems.length > 0 && (() => {
            const acctLabel = (a) => (a === 'rrsp' ? 'RRSP' : 'TFSA');
            // Fixed across themes - a status is a state, not decoration.
            const PILL = {
              match: { text: 'Transfer found', bg: '#E4F1E7', fg: '#2F6B44' },
              due: { text: 'Waiting', bg: '#FDE7EC', fg: '#BE123C' },
              transfer: { text: 'Unscheduled', bg: '#EDE6F5', fg: '#6B55A3' },
            };
            return (
              <div className="mt-7" data-testid="planner-review">
                <div className="flex items-center justify-between px-1 mb-2">
                  <p className="m-0 text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: eyebrow }}><span className="wy-star">✦ </span>To review</p>
                  <span
                    className="flex items-center justify-center text-[12px] font-extrabold text-white"
                    style={{ background: alertTone.bg, minWidth: '22px', height: '22px', borderRadius: '999px', padding: '0 7px' }}
                  >
                    {reviewItems.length}
                  </span>
                </div>
                <div className="bg-white rounded-[20px] overflow-hidden shadow-sm">
                  {reviewItems.map((it, idx) => {
                    const isTransfer = it.kind === 'transfer';
                    const pill = PILL[it.kind];
                    return (
                      <button
                        key={it.kind + ':' + (it.occ ? it.occ.occurrenceId : '') + ':' + (it.transfer ? it.transfer.id : '')}
                        type="button"
                        onClick={() => setReviewOpen(it)}
                        className="w-full flex items-center gap-3 px-3.5 py-3 text-left"
                        style={{ borderTop: idx === 0 ? 'none' : '1px solid #F3EDF1' }}
                      >
                        <span
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                          style={{ background: isTransfer ? '#E9F2E4' : track }}
                        >
                          {isTransfer ? '💸' : '📋'}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-bold text-gray-800 truncate">
                            {isTransfer ? 'Transfer to ' + acctName(it.transfer.accountId) : it.occ.sourceName}
                          </span>
                          <span className="block text-[12px] mt-0.5" style={{ color: muted }}>
                            {prettyShort(it.date)} {'·'} {isTransfer ? 'from Budget' : acctLabel(it.account)}
                          </span>
                        </span>
                        <span className="flex flex-col items-end flex-shrink-0">
                          <span className="text-[15px] font-bold text-gray-800">
                            {formatCurrency(isTransfer ? it.transfer.amount : it.occ.plannedAmount)}
                          </span>
                          <span className="mt-1 text-[10.5px] font-bold rounded-full px-2 py-0.5" style={{ background: pill.bg, color: pill.fg }}>
                            {pill.text}
                          </span>
                        </span>
                        <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-300" />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {!isSetUp && (
            <div className={cardCls + " mt-5"} style={{ padding: '20px' }}>
              <p className="m-0 mb-2 text-[15.5px]" style={labelStyle}>Start with a target</p>
              <p className="m-0 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
                Pick how much you want to put into each account this year, then add the
                contributions you already make. Worthly works out whether you'll get there.
              </p>
            </div>
          )}

          <p className="text-[11.5px] leading-relaxed px-1 mt-7 mb-1" style={{ color: '#A39FA0' }}>
            Worthly tracks what you enter here. Your contribution room, deadlines
            and what you can actually deduct may differ from the CRA's own
            records. This is not tax advice, financial advice or a promise of a
            refund. Please perform your own calculations as well and do not rely
            on the numbers calculated here when filing your taxes.
          </p>
        </>
      )}

      {!detailAccount && view === 'schedule' && (() => {
        const shown = ACCOUNTS.filter((a) => schedAccount === 'all' || schedAccount === a.key);

        // Source colours are assigned per account, so the lookup has to be too -
        // the first source in each account shares a colour, which is fine
        // because the account is named on the row whenever both are in view.
        const colorFor = (account, sourceId) => {
          const mine = local.sources.filter((x) => x.account === account);
          const i = mine.findIndex((x) => x.id === sourceId);
          return i === -1 ? accent : SOURCE_COLORS[i % SOURCE_COLORS.length];
        };

        const rows = [];
        for (const a of shown) {
          const s = summaries[a.key];
          for (const o of s.occurrences) {
            rows.push({
              id: o.occurrenceId,
              account: a.key,
              label: o.sourceName,
              date: o.date,
              amount: o.state === 'confirmed' ? o.amount : o.plannedAmount,
              state: o.state,
              color: colorFor(a.key, o.sourceId),
              occ: o,
            });
          }
          for (const c of local.contributions) {
            if (c.account !== a.key || c.occurrenceId || c.status === 'skipped') continue;
            const claimed = c.taxYear != null ? c.taxYear : taxYear;
            if (claimed !== taxYear) continue;
            const from = c.sourceId ? local.sources.find((x) => x.id === c.sourceId) : null;
            rows.push({
              id: c.id,
              account: a.key,
              label: from ? from.name : c.note || 'One-off',
              date: c.date,
              amount: Number(c.amount) || 0,
              state: 'oneoff',
              color: from ? colorFor(a.key, from.id) : '#C9C2C6',
              contrib: c,
            });
          }
        }
        rows.sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : 0));

        // Group into months in one pass, keeping the sort order rather than
        // walking a window - the two accounts' windows do not line up, so there
        // is no single range to walk.
        const months = [];
        for (const r of rows) {
          const k = r.date.slice(0, 7);
          const last = months[months.length - 1];
          if (last && last.key === k) last.rows.push(r);
          else months.push({ key: k, rows: [r] });
        }

        // Anchored to the first row dated today or later, so the rule sits ABOVE
        // today's entries and points at them. Anchoring it to the first row
        // after today put the label underneath rows of the same date, which
        // read as though those had already passed.
        const firstFromToday = rows.find((r) => r.date >= todayKey);
        const dividerBefore = firstFromToday && rows.some((r) => r.date < todayKey)
          ? firstFromToday.id
          : null;

        // One renderer for both segments, so a row looks identical whichever
        // side of today it falls on - only its surroundings change.
        const renderRow = (r, noTopBorder) => {
          const future = r.date > todayKey;
          const tag = tagForRow(r.state);
          // plan_41: Money plan look - the date in a coloured square, like your reminders
          if (planLook) {
            const sq = r.account === 'rrsp' ? ['#DFE9F9', '#3C6DB5'] : ['#DFF2E7', '#2F8756'];
            const d = Number(r.date.slice(8, 10));
            const mo = MONTH_SHORT[Number(r.date.slice(5, 7)) - 1];
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  if (r.occ) setConfirmSheet(r.occ);
                  else if (r.contrib) setOneOffSheet({ account: r.account, existing: r.contrib });
                }}
                className="w-full flex items-center text-left"
                style={{ gap: 12, padding: '11px 0', border: 0, background: 'none', borderTop: noTopBorder ? 'none' : '1px solid #EEEAF6', opacity: future ? 0.6 : 1 }}
              >
                <span className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 15, lineHeight: 1, background: sq[0], color: sq[1] }}>
                  <b style={{ fontSize: 17, fontWeight: 600 }}>{d}</b>
                  <small style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.04em', marginTop: 3, textTransform: 'uppercase' }}>{mo}</small>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 500, color: r.state === 'skipped' ? '#8B879A' : '#2B2C31' }}>{r.label}</span>
                  <span className="flex items-center" style={{ gap: 6, marginTop: 3, fontSize: 12, color: '#8B879A' }}>
                    {schedAccount === 'all' && <span>{r.account === 'rrsp' ? 'RRSP' : 'TFSA'}</span>}
                    {tag && <span style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: tag.bg, color: tag.fg }}>{tag.text}</span>}
                  </span>
                </span>
                <span className="flex-shrink-0" style={{ fontSize: 15, fontWeight: r.state === 'skipped' ? 500 : 600, color: r.state === 'skipped' ? '#A9A4B8' : '#2B2C31' }}>
                  {r.state === 'skipped' ? '—' : formatCurrency(r.amount)}
                </span>
              </button>
            );
          }
          return (
            <button
              key={r.id}
              onClick={() => {
                if (r.occ) setConfirmSheet(r.occ);
                else if (r.contrib) setOneOffSheet({ account: r.account, existing: r.contrib });
              }}
              className="w-full flex items-center gap-3 py-3 text-left"
              style={{
                borderTop: noTopBorder ? 'none' : '1px solid #F5EBF0',
                opacity: future ? 0.62 : 1,
              }}
            >
              <span className="text-[12px] font-semibold flex-shrink-0" style={{ width: '44px', color: '#8C8789' }}>
                {prettyShort(r.date)}
              </span>
              <span className="rounded-full flex-shrink-0" style={{ width: '8px', height: '8px', background: r.color }} />
              <span className="min-w-0 flex-1">
                <span
                  className="block text-[14.5px] truncate"
                  style={{ ...labelStyle, color: r.state === 'skipped' ? '#8C8789' : '#1F2937' }}
                >
                  {r.label}
                </span>
                <span className="flex items-center gap-1.5 mt-1">
                  {schedAccount === 'all' && (
                    <span className="text-[11px] font-semibold" style={{ color: '#a39fa0' }}>
                      {r.account === 'rrsp' ? 'RRSP' : 'TFSA'}
                    </span>
                  )}
                  {tag && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: tag.bg, color: tag.fg }}
                    >
                      {tag.text}
                    </span>
                  )}
                </span>
              </span>
              <span
                className="text-[14.5px] flex-shrink-0"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: r.state === 'skipped' ? 500 : 600,
                  color: r.state === 'skipped' ? '#a39fa0' : '#1F2937',
                }}
              >
                {r.state === 'skipped' ? '\u2014' : formatCurrency(r.amount)}
              </span>
            </button>
          );
        };

        const tagForRow = (state) => {
          if (planLook && state === 'confirmed') return { text: 'Confirmed', bg: '#DFF2E7', fg: '#2F8756' };
          if (planLook && state === 'due') return { text: 'Confirm', bg: '#FAE4ED', fg: '#C04B7C' };
          if (state === 'confirmed') return { text: 'Confirmed', bg: '#EEF5E9', fg: '#2F6B44' };
          if (state === 'skipped') return { text: 'Skipped', bg: '#F2EEF0', fg: '#6B7280' };
          if (state === 'due') return { text: 'Confirm', bg: '#FEE9EE', fg: '#BE123C' };
          if (state === 'oneoff') return { text: 'One-off', bg: tilePalette[3].bg, fg: tilePalette[3].value };
          return null;
        };

        const rrspIn = shown.some((a) => a.key === 'rrsp');

        return (
          <>
            <div className={planLook ? 'flex mb-5' : 'flex gap-2 mb-6'} style={planLook ? { background: '#ECE8F4', borderRadius: 999, padding: 3 } : undefined}>
              {[{ k: 'all', label: 'All' }, { k: 'rrsp', label: 'RRSP' }, { k: 'tfsa', label: 'TFSA' }].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setSchedAccount(f.k)}
                  className="flex-1 rounded-full py-2.5 text-[13px] font-semibold"
                  style={
                    planLook
                      ? (schedAccount === f.k ? { background: '#FFFFFF', color: '#2B2C31' } : { background: 'transparent', color: '#6C6879', fontWeight: 500 })
                      : schedAccount === f.k
                      ? { background: accent, color: '#fff' }
                      : { background: '#fff', color: '#8C8789', boxShadow: '0 2px 10px rgba(120,60,95,.06)' }
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className={cardCls + " p-6 text-center"}>
                <p className="m-0 text-[13.5px] leading-relaxed" style={{ color: '#8C8789' }}>
                  Nothing scheduled for {taxYear} yet. Add a source on the Overview tab and
                  its contributions will appear here, all the way to the deadline.
                </p>
              </div>
            ) : (
              months.map((mo) => {
                const y = Number(mo.key.slice(0, 4));
                const m = Number(mo.key.slice(5, 7));
                // Sorted, so what is still to come is always the tail.
                const settled = mo.rows.filter((r) => r.date <= todayKey);
                const ahead = mo.rows.filter((r) => r.date > todayKey);
                return (
                  <div key={mo.key} className="mb-6">
                    <Eyebrow>{MONTH_SHORT[m - 1]} {y}</Eyebrow>
                    {/* No vertical padding: the shaded region bleeds to the card
                        edges, and 2px here leaves a white sliver above and below it.
                        Rows carry their own padding. */}
                    <div className={cardCls + " overflow-hidden"} style={{ padding: '0 16px' }}>
                      {settled.map((r, i) => (
                        <div key={r.id}>
                          {dividerBefore === r.id && (
                            <div className="flex items-center gap-2.5 py-3">
                              <span className="flex-1 rounded-full" style={{ height: planLook ? '2px' : '1.5px', background: planLook ? '#ECE6FA' : accent, opacity: planLook ? 1 : 0.3 }} />
                              <span className="text-[11px] font-bold tracking-[0.06em]" style={{ color: planLook ? '#7A5DD3' : accent }}>
                                TODAY {'\u00b7'} {prettyShort(todayKey).toUpperCase()}
                              </span>
                              <span className="flex-1 rounded-full" style={{ height: '1.5px', background: accent, opacity: 0.3 }} />
                            </div>
                          )}
                          {renderRow(r, i === 0 || dividerBefore === r.id)}
                        </div>
                      ))}

                      {ahead.length > 0 && (
                        <div style={{ background: planLook ? 'transparent' : '#F7F5F7', margin: '0 -16px', padding: '0 16px' }}>
                          {ahead.map((r, i) => renderRow(r, i === 0))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {rrspIn && (
              <div className="flex gap-3 items-start p-3.5 rounded-2xl mt-2" style={{ background: tilePalette[0].bg }}>
                <span className="text-[16px] leading-none flex-shrink-0" style={{ marginTop: '2px' }}>{'\ud83d\udcc5'}</span>
                <p className="m-0 text-[13px] leading-snug" style={{ color: tilePalette[0].value }}>
                  <b className="block text-[13.5px] mb-0.5">
                    RRSP deadline {'\u00b7'} {prettyDate(summaries.rrsp.window.end)}
                  </b>
                  Anything you put in up to this date can still count toward {taxYear}.
                </p>
              </div>
            )}
            <div style={{ height: '8px' }} />
          </>
        );
      })()}

      {linkSheet && (
        <LinkSheet
          account={linkSheet.account}
          investmentAccounts={investmentAccounts}
          linked={(local.accounts[linkSheet.account] || {}).linkedAccountIds || []}
          otherLinked={(local.accounts[linkSheet.account === 'rrsp' ? 'tfsa' : 'rrsp'] || {}).linkedAccountIds || []}
          accent={accent}
          track={track}
          onSave={(ids) => saveLinks(linkSheet.account, ids)}
          onClose={() => setLinkSheet(null)}
        />
      )}
      {reviewOpen && (() => {
        const it = reviewOpen;
        const close = () => setReviewOpen(null);
        const acctLabel = (a) => (a === 'rrsp' ? 'RRSP' : 'TFSA');
        const softBg = isWarm ? '#F0E6D8' : isPink ? '#EDE6F5' : '#EEF1FE';
        const softInk = isWarm ? '#2D5A3D' : isPink ? '#6B55A3' : '#4361EE';
        // Same buttons as the planner's Confirm sheet ("Did this go in?").
        const mainCls = 'w-full rounded-full py-4 text-[15px] font-bold text-white';
        const main = { background: accent };
        const secondCls = 'w-full rounded-full py-3.5 text-[14px] font-semibold border theme-border-100';
        const second = { color: muted };
        const box = (label, value, tone) => (
          <div
            className="flex items-center justify-between rounded-2xl px-4 py-3 mb-2 border theme-border-100"
            style={{ background: '#FCF9FB', color: '#1F2937', ...(tone || {}) }}
          >
            <span className="text-[13.5px]">{label}</span>
            <span className="text-[15px] font-bold">{value}</span>
          </div>
        );
        const title = it.kind === 'transfer' ? 'Transfer to ' + acctName(it.transfer.accountId) : it.occ.sourceName;
        const subtitle = it.kind === 'transfer'
          ? prettyShort(it.transfer.date) + ' · from your Budget'
          : 'Scheduled ' + prettyShort(it.occ.date) + ' · ' + acctLabel(it.account);
        return (
          <Sheet title={title} subtitle={subtitle} onClose={close} track={track}>
            {it.kind !== 'transfer' && box('Planned', formatCurrency(it.occ.plannedAmount))}
            {it.kind === 'match' && box('💸 Transfer found · ' + prettyShort(it.transfer.date), formatCurrency(it.transfer.amount), { color: '#2F6B44' })}
            {it.kind === 'due' && (
              <p className="m-0 mb-2 text-[13px]" style={{ color: muted }}>No transfer found in your Budget for this one yet.</p>
            )}
            {it.kind === 'transfer' && (
              <>
                {box('Amount', formatCurrency(it.transfer.amount))}
                <p className="m-0 mb-2 text-[13px]" style={{ color: muted }}>
                  Nothing you scheduled matches this transfer. There is no contribution within {TRANSFER_MATCH_DAYS} days and ${TRANSFER_MATCH_AMOUNT} of this amount.
                </p>
              </>
            )}
            <div className="flex flex-col gap-2.5 mt-4">
              {it.kind === 'match' && (
                <>
                  <button type="button" className={mainCls} style={main} onClick={() => { acceptMatch(it, it.occ); close(); }}>
                    Confirm {formatCurrency(it.transfer.amount)} transfer
                  </button>
                  <button type="button" className={secondCls} style={second} onClick={() => { rejectMatch(it); close(); }}>
                    Not this one
                  </button>
                </>
              )}
              {it.kind === 'due' && (
                <>
                  <button type="button" className={mainCls} style={main} onClick={() => { confirmOccurrence(it.occ, it.occ.plannedAmount); close(); }}>
                    Confirm {formatCurrency(it.occ.plannedAmount)}
                  </button>
                  <button type="button" className={secondCls} style={second} onClick={() => { skipOccurrence(it.occ); close(); }}>
                    Skip
                  </button>
                </>
              )}
              {it.kind === 'transfer' && (
                <>
                  <button type="button" className={mainCls} style={main} onClick={() => { acceptOneOff(it); close(); }}>
                    Add as one-off
                  </button>
                  <button type="button" className={secondCls} style={second} onClick={() => { ignoreTransfers([it.transfer.id]); close(); }}>
                    Ignore
                  </button>
                </>
              )}
              {it.kind !== 'transfer' && (
                <button type="button" className="w-full py-2 text-[13px] font-semibold" style={{ color: muted }} onClick={() => { close(); setConfirmSheet(it.occ); }}>
                  Confirm a different amount
                </button>
              )}
            </div>
          </Sheet>
        );
      })()}
      {showInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowInfo(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl overflow-y-auto" style={{ maxHeight: '88vh', touchAction: 'pan-y' }}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About the Contributions Planner</h3>
            <div className="space-y-4">
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🎯</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">What this is for</p><p className="text-xs text-gray-500 leading-relaxed">See how your contributions add up against your room and the goal you set. The planner lives in Wealth → Invest, above your investment accounts.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">It tracks what you put in, not what the accounts are worth. For their balances, see Your Investment Accounts in Wealth → Invest.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🔗</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Linked accounts</p><p className="text-xs text-gray-500 leading-relaxed">On the RRSP or TFSA page, link the investment accounts you transfer into in your Budget. You can link more than one, but each account can only belong to RRSP or TFSA.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">From the day you link, every transfer into them shows up under To review. Anything before that is left alone, because you will have recorded it already.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">Nothing is confirmed until you tap, and the planner never changes your Budget or Wealth. Contributions taken straight off your pay, like a workplace RRSP, never go through your Budget, so confirm those by hand.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🔔</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">To review</p><p className="text-xs text-gray-500 leading-relaxed">Everything in To review is waiting for a yes or no. Tap an item to decide what to do with it.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">A green Transfer found label means a scheduled contribution has a transfer from your Budget within 7 days and $5 of it. You can confirm the amount that actually went in, or tap Not this one if they aren’t the same thing.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">A red Waiting label means the date of a scheduled contribution has passed and no matching transfer was found. You can confirm it, skip it, or confirm a different amount.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">A purple Unscheduled label means a transfer was found that doesn’t match anything you scheduled. You can add it as a one-off contribution or ignore it.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">The same number shows as an insight on your Home screen, so you don’t need to come here to check.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🗓️</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">The RRSP year is not a calendar year</p><p className="text-xs text-gray-500 leading-relaxed">It runs from early March through to the deadline the following March. The planner counts contributions scheduled in January and February toward the previous tax year.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🚪</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Room is whatever you enter</p><p className="text-xs text-gray-500 leading-relaxed">Enter your contribution room for each account type as shown on your notice of assessment and/or CRA account. Worthly cannot see your CRA account. It also doesn't carry forward room for each year, so this will need to be re-entered by you.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">➕</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">One-offs are supported, not the point</p><p className="text-xs text-gray-500 leading-relaxed">Log a bonus or a lump sum any time, or let a linked account suggest it from your Budget. This page is built around recurring contributions.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">⚠️</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Check the numbers yourself</p><p className="text-xs text-gray-500 leading-relaxed">Everything here is worked out from what you enter. It is not tax advice and not a promise of a refund. Check your notice of assessment, your CRA account, and do your own calculations before you file.</p></div></div>
            </div>
            <button onClick={() => setShowInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
          </div>
        </div>,
        document.body
      )}
      {confirmSheet && (
        <ConfirmSheet
          occ={confirmSheet}
          accent={accent}
          track={track}
          muted={muted}
          onConfirm={(amt) => confirmOccurrence(confirmSheet, amt)}
          onSkip={() => skipOccurrence(confirmSheet)}
          onUnconfirm={() => unconfirmOccurrence(confirmSheet)}
          onClose={() => setConfirmSheet(null)}
        />
      )}
      {targetSheet && (
        <TargetSheet
          account={targetSheet.account}
          taxYear={taxYear}
          config={local.accounts[targetSheet.account]}
          accent={accent}
          track={track}
          muted={muted}
          palette={tilePalette}
          onSave={saveTarget}
          onClose={() => setTargetSheet(null)}
        />
      )}
      {oneOffSheet && (
        <OneOffSheet
          account={oneOffSheet.account}
          taxYear={taxYear}
          todayKey={todayKey}
          accent={accent}
          track={track}
          muted={muted}
          sources={local.sources.filter((s) => s.account === oneOffSheet.account)}
          existing={oneOffSheet.existing || null}
          onSave={logOneOff}
          onDelete={(id) => { removeContribution(id); setOneOffSheet(null); }}
          onClose={() => setOneOffSheet(null)}
        />
      )}
      {sourceSheet && (
        <SourceSheet
          account={sourceSheet.account}
          source={sourceSheet.source}
          todayKey={todayKey}
          accent={accent}
          track={track}
          muted={muted}
          palette={tilePalette}
          onSave={saveSource}
          onStop={stopSource}
          onResume={resumeSource}
          onDelete={deleteSource}
          onClose={() => setSourceSheet(null)}
        />
      )}
    </div>
  );
};

export default PlansTab;
